import { useEffect, useState, useMemo, useRef } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash, Eye, MagnifyingGlass, UserPlus, CaretLeft, CaretRight, Printer, Warning, Clock, CaretDown, CaretUp, ClipboardText, EnvelopeSimple } from '@phosphor-icons/react';
import { DateInput } from '../components/DateInput';
import { formatDatePrint } from '../utils/dateFormat';
import { printHTML, downloadWord } from '../utils/printHelper';
import { confirmDialog } from '@/components/ConfirmDialog';

const NIVO_OBRAZOVANJA = [
  'Bez obrazovanja', 'Nepotpuna osnovna škola', 'Osnovna škola',
  'Srednja škola - trogodišnja', 'Srednja škola - četvorogodišnja', 'Gimnazija',
  'Viša škola', 'Visoka škola - Bachelor', 'Visoka škola - Master', 'Doktorat',
];

const OPSTINE_CRNE_GORE = [
  'Andrijevica', 'Bar', 'Berane', 'Bijelo Polje', 'Budva', 'Cetinje',
  'Danilovgrad', 'Gusinje', 'Herceg Novi', 'Kolašin', 'Kotor', 'Mojkovac',
  'Nikšić', 'Petnjica', 'Plav', 'Pljevlja', 'Plužine', 'Podgorica',
  'Rožaje', 'Šavnik', 'Tivat', 'Tuzi', 'Ulcinj', 'Zeta', 'Žabljak',
];

const VRSTA_UGOVORA = [
  { value: 'ugovor_neodredjeno', label: 'Ugovor o radu na neodređeno' },
  { value: 'ugovor_odredjeno', label: 'Ugovor o radu na određeno' },
  { value: 'ugovor_prekovremeni', label: 'Ugovor o prekovremenom radu' },
  { value: 'ugovor_dopunski', label: 'Ugovor o dopunskom radu' },
  { value: 'rjesenje_odmor', label: 'Rješenje godišnjeg odmora' },
];

const PAGE_SIZE = 50;

// Izračuna razliku između dvije datuma u formatu GG.MM.DD
export function calcDurationYMD(fromDate, toDate) {
  if (!fromDate) return '';
  const from = new Date(fromDate);
  const to = toDate ? new Date(toDate) : new Date();
  if (isNaN(from.getTime()) || isNaN(to.getTime())) return '';
  if (to < from) return '';
  // "Radni staž" convention (Zakon o radu CG): the termination date counts as a worked day,
  // so the end date is INCLUSIVE. 01.07 – 31.08 => 2 months (not 1m 30d).
  // We shift the end date forward by 1 day and then do normal exclusive date-diff math.
  const endExclusive = new Date(to.getFullYear(), to.getMonth(), to.getDate() + 1);
  let years = endExclusive.getFullYear() - from.getFullYear();
  let months = endExclusive.getMonth() - from.getMonth();
  let days = endExclusive.getDate() - from.getDate();
  if (days < 0) {
    months -= 1;
    const prevMonth = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), 0);
    days += prevMonth.getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(years)}.${pad(months)}.${pad(days)}`;
}

// Format salary as "700,00 EUR" (Montenegrin convention: comma as decimal separator)
const formatZarada = (val) => {
  if (val === null || val === undefined || val === '') return '________';
  const s = String(val).replace(/\./g, '').replace(',', '.').trim();
  const n = parseFloat(s);
  if (isNaN(n)) return String(val);
  return n.toFixed(2).replace('.', ',') + ' EUR';
};

// Standard 4-line document header used across every HR/termination template.
// Example output:
//   "2FAST2FURIOUS" DOO
//   ULCINJ, PINJEŠ 2, STAN 11
//   PIB: 03536475
//   Ulcinj, 10.10.2026.GOD.
export function renderDocHeader(client, dateOverride) {
  const d = dateOverride ? new Date(String(dateOverride).substring(0, 10) + 'T00:00:00') : new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const datumStr = `${dd}.${mm}.${yyyy}.GOD.`;

  // Prefer skraćeni naziv (short name) for the header line; fall back to full name.
  const nameRaw = (client?.skraceni_naziv || client?.naziv_firme || '____________').toUpperCase().trim();
  // Append legal form suffix (DOO/NVO) if not already present in the short name.
  let firmaLine = nameRaw;
  const hasLegalSuffix = /\b(DOO|D\.O\.O\.|AD|OD|PR|NVO|UDRUŽ)\b/i.test(nameRaw);
  if (!hasLegalSuffix) {
    const suffixMap = { doo: 'DOO', nvo: 'NVO' };
    const sfx = suffixMap[(client?.tip_lica || 'doo').toLowerCase()];
    if (sfx) firmaLine = `${nameRaw} ${sfx}`;
  }

  const adresa = (client?.adresa || '').toUpperCase().trim();
  const grad = (client?.grad || '').toUpperCase().trim();
  const addressLine = [grad, adresa].filter(Boolean).join(', ') || '____________';
  const gradMjesto = client?.grad || '____________';
  const pib = client?.pib || '________________';

  return `<div class="doc-header">
<div class="firma-line"><b>${firmaLine}</b></div>
<div>${addressLine}</div>
<div>PIB: ${pib}</div>
<div>${gradMjesto}, ${datumStr}</div>
</div>`;
}

// Shared CSS for the standard document header (included inside <style> blocks of each generator).
export const DOC_HEADER_CSS = `
.doc-header { margin-bottom: 14px; font-size: 11pt; line-height: 1.4; }
.doc-header div { margin: 1px 0; }
.doc-header .firma-line { font-size: 11.5pt; }
`;

const emptyForm = {
  client_id: '', fizicko_lice_id: '', vrsta_lica: 'domace', jmb: '', ime: '', prezime: '',
  prebivaliste: '', nivo_obrazovanja: '', kvalifikacija: '', nivo_obrazovanja_rm: '', kvalifikacija_rm: '',
  naziv_radnog_mjesta: '', opis_poslova: '', mjesto_rada: '',
  radno_vrijeme: '', datum_sticanja: '',
  tip_rada: 'neodredjeno', datum_isteka: '', osnov_odredjeno: '', datum_odjave: '',
  vrsta_ugovora: 'ugovor_neodredjeno', zarada: '', napomena: '', status: 'aktivan',
};

// ========== CONTRACT PRINT TEMPLATES ==========

function generateUgovorNeodredjeno(entry, client) {
  const radnoVrijeme = parseInt(entry.radno_vrijeme) || 40;
  const isPuno = radnoVrijeme >= 40;
  // Korisnik je tražio da Naziv radnog mjesta, Sati nedjeljno, Datum sticanja,
  // Datum isteka i Zarada budu BOLD + UNDERLINE u ugovoru. Wrap-ujemo vrijednost
  // sa <u><b>...</b></u> tako da svako mjesto gdje se pojavljuje bude formatirano.
  const radnoVrijemeText = isPuno
    ? `sa punim radnim vremenom od <u><b>40 časova nedjeljno</b></u>`
    : `sa nepunim radnim vremenom od <u><b>${radnoVrijeme} časova nedjeljno</b></u>`;

  const firma = client?.naziv_firme || '________________________________________';
  const direktor = client?.direktor || '________________________________________';
  const grad = client?.grad || '________________';
  const zarada = `<u><b>${formatZarada(entry.zarada)}</b></u>`;
  const datumSticanja = `<u><b>${formatDatePrint(entry.datum_sticanja) || '__.__.____'}</b></u>`;
  const zaposleni = `${(entry.prezime || '').toUpperCase()} ${(entry.ime || '').toUpperCase()}`;
  const jmb = entry.jmb || '________________';
  const kvalifikacija = entry.kvalifikacija || '';
  const radnoMjesto = `<u><b>${(entry.naziv_radnog_mjesta || '').toUpperCase()}</b></u>`;
  const prebivaliste = entry.prebivaliste || '';
  const nivo = entry.nivo_obrazovanja || '';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Ugovor na neodređeno - ${zaposleni}</title>
<style>
@page { size: A4; margin: 15mm 18mm; }
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family: Arial, Helvetica, sans-serif; font-size: 12pt; color: #000; line-height: 1.5; }
.header { font-size: 10pt; text-align: justify; line-height: 1.4; margin-bottom: 16px; }
.title { text-align: center; font-weight: bold; font-size: 14pt; margin: 14px 0 4px; text-transform: uppercase; }
.subtitle { text-align: center; font-size: 12pt; margin-bottom: 14px; }
.party { margin: 6px 0; font-size: 12pt; line-height: 1.5; }
.clan-title { font-weight: bold; text-align: center; margin: 12px 0 4px; font-size: 12pt; }
.clan-body { text-align: justify; margin: 0 0 4px; font-size: 12pt; line-height: 1.5; }
.clan-body ul { margin-left: 12px; }
.clan-body li { margin-bottom: 2px; }
.section-title { font-weight: bold; text-align: center; font-size: 12pt; margin: 14px 0 6px; font-style: italic; }
.signatures { display: flex; justify-content: space-between; margin-top: 50px; }
.sig-block { text-align: center; font-size: 12pt; }
.sig-line { border-top: 1px solid #000; min-width: 160px; margin-top: 50px; padding-top: 4px; }
.mp { margin-top: 50px; font-size: 12pt; }
@media print { body { margin:0; padding:0; } }
</style></head><body>

<div class="header">
Na osnovu člana 36 u vezi sa članom 29 Zakona o radu ("Službeni list Crne Gore", br. 074/19 od 30.12.2019, 008/21 od 26.01.2021, 059/21 od 04.06.2021, 068/21 od 23.06.2021, 145/21 od 31.12.2021), Opšteg kolektivnog ugovora (\u201eSlužbeni list Crne Gore\u201c, br. 150/2022 od 30.12.2022), člana 1 Zakona o zaštiti na radu ("Sl. list RCG", br. 79/04 od 23.12.2004 i "Sl. list Crne Gore", br. 26/10 od 07.05.2010, 40/11 od 08.08.2011) i Pravilnika o pitanjima iz zaštite na radu koje treba urediti ugovorom o radu (\u201eSl.list RCG br.67/05\u201c) zaključuje se:
</div>

<div class="title">UGOVOR O RADU NA NEODREĐENO VRIJEME</div>
<div class="subtitle">zaključen između</div>

<p class="party">1. ${firma}, ${direktor} (u daljem tekstu poslodavac) i</p>
<p class="party">2. ${zaposleni}, ${prebivaliste ? prebivaliste + ', ' : ''}${grad}, JMBG ${jmb}, ${nivo}${kvalifikacija ? ', ' + kvalifikacija : ''}, (u daljem tekstu: zaposleni)</p>

<div class="clan-title">Član 1.</div>
<div class="clan-body">Zaposleni zasniva radni odnos na radno mjesto ${radnoMjesto}. Pored opštih uslova za zasnivanje radnog odnosa radnik ispunjava i posebne uslove za rad i to stručna sprema, radno iskustvo i dr.</div>

<div class="clan-title">Član 2.</div>
<div class="clan-body">Na predmetnom radnom mjestu ZAPOSLENI/A je dužan da:
<ul>
<li>savjesno i odgovorno obavlja poslove radnog mjesta,</li>
<li>poštuje organizaciju rada i poslovanje kod POSLODAVCA, kao i uslove i pravila POSLODAVCA u vezi sa ispunjavanjem ugovorenih i drugih obaveza iz radnog odnosa,</li>
<li>vodi računa i savjesno se odnosi prema sredstvima rada i materijalnim sredstvima POSLODAVCA,</li>
<li>u potpunosti poštuje propise o zaštiti i zdravlju na radu.</li>
<li>Obavlja i ostale poslove iz djelokruga radnog mjesta prema nalogu pretpostavljenih, kao oblik opšte obaveze lojalnosti prema POSLODAVCU i poštovanja obaveza koje proizlaze iz radnog odnosa.</li>
</ul></div>

<div class="clan-title">Član 3.</div>
<div class="clan-body">ZAPOSLENI/A zasniva radni odnos na neodređeno vrijeme, počev od ${datumSticanja} godine u organizacionoj jedinici ${firma} u ${grad}u, s tim što u skladu sa potrebama rada u određenom vremenskom periodu (turistička sezona) može biti raspoređen/a i u drugu organizacionu jedinicu na teritoriji opštine ${grad}.</div>

<div class="clan-title">Član 4.</div>
<div class="clan-body">ZAPOSLENI/A zasniva radni odnos ${radnoVrijemeText}. Rad se obavlja u smjenama prema mjesečnom rasporedu koji će uraditi POSLODAVAC.<br>
Radi racionalnijeg korišćenja radnog vremena POSLODAVAC može izvršiti preraspodjelu radnog vremena u skladu sa članom 68 Zakona o radu.</div>

<div class="clan-title">Član 5.</div>
<div class="clan-body">ZAPOSLENI/A ima pravo na odmor i to:
<ul>
<li>U toku radnog dana u trajanju od najmanje 30 minuta (pauza). Odluku o rasporedu i načinu korišćenja odmora u toku radnog dana donosi nadležni organ POSLODAVCA,</li>
<li>Dnevni odmor između dva uzastopna radna dana u trajanju od najmanje 12 časova neprekidno,</li>
<li>Sedmični odmor u trajanju od najmanje 24 časa, koji se u pravilu koristi nedjeljom. Ako priroda posla i organizacija rada to zahtijeva, POSLODAVAC je dužan da odredi drugi dan za korišćenje ovog odmora, o čemu unaprijed za naredni mjesec sačinjava raspored i blagovremeno obavještava ZAPOSLENOG/U,</li>
<li>Godišnji odmor u trajanju od najmanje dvadeset (20) radnih dana. Trajanje godišnjeg odmora utvrđuje se srazmjerno vremenu provedenom u radnom odnosu, s tim da se obračunava i uvećava na osnovu kriterijuma utvrđenih Zakonom o radu i Opštim kolektivnim ugovorom.</li>
</ul></div>

<div class="clan-title">Član 6.</div>
<div class="clan-body">Zarada ZAPOSLENOG/E utvrđena je u neto iznosu od ${zarada} koji predstavlja iznos minimalne zarade u skladu sa Zakonom o radu i isplaćivaće se najkasnije do 10-og u mjesecu za prethodni mjesec rada.<br>
Iznos minimalne zarade iz stava 1 ovog člana utvrđuje Vlada Crne Gore.</div>

<div class="clan-title">Član 7.</div>
<div class="clan-body">ZAPOSLENI/A je dužan da se na radu pridržava obaveza propisanih Zakonom o radu, Opštim kolektivnim ugovorom i ugovorom o radu.<br>
ZAPOSLENI/A koji umišljajem ili iz nehata povrijedi radnu obavezu ili se ne pridržava odluka, usmenih naloga ili uputstava koje je donio POSLODAVAC, odgovara za učinjenu povredu radne obaveze.</div>

<div class="clan-title">Član 8.</div>
<div class="clan-body">Radni odnos ZAPOSLENOM/OJ može prestati:
<ul>
<li>po sili zakona,</li>
<li>sporazumom između ZAPOSLENOG/E i POSLODAVCA, i</li>
<li>otkazom ugovora o radu od strane POSLODAVCA ili ZAPOSLENOG/E.</li>
</ul>
ZAPOSLENI/A ima pravo i dužnost da, u slučaju prestanka radnog odnosa, ostane na radu u trajanju od najmanje 30 dana od dana dostavljanja otkaza ugovora o radu, odnosno odluke o prestanku radnog odnosa (otkazni rok).</div>

<div class="clan-title">Član 9.</div>
<div class="clan-body">Za sva ostala prava i obaveze između ZAPOSLENOG/E i POSLODAVCA koja nijesu utvrđena ovim Ugovorom o radu primjenjivaće se odredbe iz Zakona o radu i Opšteg kolektivnog ugovora koji su na snazi u momentu utvrđivanja prava između ZAPOSLENOG/E i POSLODAVCA.</div>

<div class="section-title">MJERE ZAŠTITE I ZDRAVLJA NA RADU, NJIHOVO SPROVOĐENJE, PRAVA, OBAVEZE I ODGOVORNOSTI ZAPOSLENOG/E</div>

<div class="clan-title">Član 10.</div>
<div class="clan-body">POSLODAVAC je dužan da organizacijom rada i procesa rada obezbijedi ZAPOSLENOM/OJ bezbjedne i zdrave uslove rada, u skladu sa zakonom, podzakonskim aktima, Aktom o procjeni rizika, tehničkim propisima, standardima i pravilima struke.<br>
POSLODAVAC je dužan da ZAPOSLENOG/U, prije početka rada, upozna sa svim opasnostima, štetnostima i rizicima na radnom mjestu na koje je raspoređen, kao i sa konkretnim mjerama zaštite i zdravlja na radu potrebnim radi sprečavanja povrede na radu, profesionalne bolesti ili bolesti u vezi sa radom.<br>
POSLODAVAC je dužan da ZAPOSLENOM/OJ obezbijedi odgovarajuća pisana uputstva za bezbjedan i zdrav rad.</div>

<div class="clan-title">Član 11.</div>
<div class="clan-body">POSLODAVAC je dužan da izvrši teorijsko i praktično osposobljavanje ZAPOSLENOG/E za bezbjedan i zdrav rad:<br>
– prije raspoređivanja na radno mjesto;<br>
– prilikom raspoređivanja na drugo radno mjesto;<br>
– prilikom uvođenja nove tehnologije;<br>
– prilikom uvođenja novih ili zamjene postojećih sredstava za rad;<br>
– prilikom promjene procesa rada; i<br>
– prilikom ponovnog raspoređivanja na rad nakon odsustva sa rada koje je trajalo duže od jedne godine.<br>
Osposobljavanje i provjera osposobljenosti obavljaju se u toku radnog vremena, a sve troškove snosi POSLODAVAC.</div>

<div class="clan-title">Član 12.</div>
<div class="clan-body">ZAPOSLENI/A ima pravo i obavezu da se prije početka rada upozna sa mjerama zaštite i zdravlja na radu na poslovima radnog mjesta na koje je raspoređen i da se osposobljava za njihovo sprovođenje.<br>
ZAPOSLENI/A ima pravo i obavezu da POSLODAVCU daje predloge, primjedbe i obavještenja o pitanjima koja se odnose na zaštitu i zdravlje na radu.<br>
ZAPOSLENI/A ima pravo i obavezu da obavi prethodni, periodični ili vanredni zdravstveni pregled koji odgovara rizicima radnog mjesta, kada ga na pregled uputi POSLODAVAC.</div>

<div class="clan-title">Član 13.</div>
<div class="clan-body">ZAPOSLENI/A ima pravo da odbije da radi ako:<br>
– prethodno nije upoznat/a sa opasnostima, štetnostima i rizicima na radu;<br>
– mu/joj POSLODAVAC nije obezbijedio propisani zdravstveni pregled;<br>
– mu/joj prijeti neposredna opasnost po život ili zdravlje zato što nijesu sprovedene propisane mjere zaštite i zdravlja na radu; ili<br>
– na sredstvu za rad nijesu obezbijeđene propisane mjere zaštite i zdravlja na radu, čime su neposredno ugroženi njegov/njen život i zdravlje.<br>
Kada odbije da radi, ZAPOSLENI/A je dužan/na da se pisanim putem obrati POSLODAVCU radi preduzimanja mjera za koje smatra da nijesu sprovedene.<br>
Ako POSLODAVAC smatra da zahtjev ZAPOSLENOG/E nije opravdan, dužan je da odmah obavijesti nadležnu inspekciju zaštite i zdravlja na radu.</div>

<div class="clan-title">Član 14.</div>
<div class="clan-body">U slučaju ozbiljne i neposredne opasnosti po život i zdravlje, ZAPOSLENI/A može preduzeti odgovarajuće mjere u skladu sa svojim znanjem, osposobljenošću i tehničkim sredstvima koja su mu/joj na raspolaganju.<br>
U slučaju ozbiljne, neposredne i neizbježne opasnosti, ZAPOSLENI/A ima pravo da zaustavi rad i napusti opasno radno mjesto, radni proces, odnosno radnu sredinu i uputi se na bezbjedno mjesto.<br>
Zbog postupanja u skladu sa ovim članom ZAPOSLENI/A ne smije biti doveden/a u nepovoljniji položaj, osim ako je postupao/la nepažljivo ili nemarno.</div>

<div class="clan-title">Član 15.</div>
<div class="clan-body">ZAPOSLENI/A je obavezan/na da, u skladu sa svojom osposobljenošću i uputstvima dobijenim od POSLODAVCA:<br>
– primjenjuje propisane mjere zaštite i zdravlja na radu;<br>
– vodi računa o svojoj bezbjednosti i zdravlju, kao i o bezbjednosti i zdravlju drugih zaposlenih i lica na koja može uticati njegov/njen rad;<br>
– pravilno i namjenski koristi sredstva za rad, mašine, uređaje, alate, opasne materije i zaštitne uređaje;<br>
– pravilno i namjenski koristi sredstva i opremu lične zaštite na radu;<br>
– ne uklanja, ne isključuje, ne mijenja i ne oštećuje zaštitne uređaje postavljene na sredstvima za rad;<br>
– poštuje znakove upozorenja, zabrane i obaveznog postupanja; i<br>
– sarađuje sa POSLODAVCEM i stručnim licem za zaštitu i zdravlje na radu radi sprovođenja propisanih mjera.</div>

<div class="clan-title">Član 16.</div>
<div class="clan-body">ZAPOSLENI/A je dužan/na da odmah, pisano ili usmeno, neposredno ili preko predstavnika zaposlenih, obavijesti POSLODAVCA i stručno lice o:<br>
– nepravilnostima i nedostacima na sredstvima za rad;<br>
– oštećenju sredstava i opreme lične zaštite;<br>
– štetnostima i opasnostima u radnoj sredini;<br>
– povredi na radu ili opasnoj pojavi; i<br>
– svakoj drugoj pojavi koja može ugroziti njegov/njen život i zdravlje ili život i zdravlje drugih lica.<br>
Ako POSLODAVAC ne otkloni prijavljene nepravilnosti, štetnosti ili opasnosti u roku od tri dana ili ZAPOSLENI/A smatra da nijesu preduzete odgovarajuće mjere, ZAPOSLENI/A može zatražiti intervenciju nadležne inspekcije zaštite i zdravlja na radu i o tome obavijestiti stručno lice.</div>

<div class="clan-title">Član 17.</div>
<div class="clan-body">ZAPOSLENI/A ne može započeti niti obavljati rad pod uticajem alkohola, droge ili drugih sredstava zavisnosti.<br>
ZAPOSLENI/A je dužan/na da se podvrgne provjeri da li je pod uticajem sredstava zavisnosti, na način i po postupku utvrđenom zakonom i odgovarajućim aktom POSLODAVCA.<br>
O izvršenoj provjeri sačinjava se odgovarajući zapisnik.</div>

<div class="clan-title">Član 18.</div>
<div class="clan-body">ZAPOSLENI/A koji radi na radnom mjestu sa posebnim uslovima rada ili povećanim rizikom dužan/na je da odmah obavijesti POSLODAVCA ako ocijeni da zbog svog zdravstvenog stanja nije sposoban/na da bezbjedno obavlja te poslove.<br>
ZAPOSLENI/A je dužan/na da se odazove na zdravstveni pregled na koji ga/je uputi POSLODAVAC.<br>
Ako se zdravstvenim pregledom utvrdi da ZAPOSLENI/A ne ispunjava posebne zdravstvene uslove za obavljanje poslova radnog mjesta, POSLODAVAC će postupiti u skladu sa zakonom i aktom o sistematizaciji radnih mjesta.</div>

<div class="clan-title">Član 19.</div>
<div class="clan-body">ZAPOSLENI/A je dužan/na da svaku povredu na radu odmah prijavi POSLODAVCU ili neposrednom rukovodiocu, osim ako zbog težine povrede to nije u mogućnosti.<br>
POSLODAVAC je dužan da povredu na radu prijavi nadležnim organima u slučajevima i rokovima propisanim zakonom i da povrijeđenom zaposlenom i zdravstvenoj ustanovi u kojoj je izvršen pregled izda propisani izvještaj o povredi na radu.<br>
ZAPOSLENI/A ostvaruje prava po osnovu povrede na radu, profesionalne bolesti ili bolesti u vezi sa radom u skladu sa propisima kojima se uređuju zdravstveno, penzijsko i invalidsko osiguranje i radni odnosi.</div>

<div class="clan-title">Član 20.</div>
<div class="clan-body">ZAPOSLENI/A podliježe odgovornosti ako se ne pridržava propisanih mjera zaštite i zdravlja na radu ili ne izvršava obaveze utvrđene zakonom, ovim ugovorom, Aktom o procjeni rizika, uputstvima za bezbjedan rad i drugim aktima POSLODAVCA.<br>
Postojanje odgovornosti, vrsta mjere i postupak njenog utvrđivanja određuju se u skladu sa Zakonom o radu, kolektivnim ugovorom i aktima POSLODAVCA.<br>
Obaveze ZAPOSLENOG/E u oblasti zaštite i zdravlja na radu ne oslobađaju POSLODAVCA njegove zakonske odgovornosti za obezbjeđivanje bezbjednih i zdravih uslova rada.</div>

<div class="clan-title">Član 21.</div>
<div class="clan-body">POSLODAVAC je dužan da organizuje pružanje prve pomoći, zaštitu od požara, spašavanje i evakuaciju zaposlenih, da odredi i osposobi odgovarajući broj zaposlenih za sprovođenje tih mjera i obezbijedi potrebna sredstva i opremu.<br>
ZAPOSLENI/A je dužan/na da se upozna sa postupkom pružanja prve pomoći, zaštite od požara i evakuacije i da postupa prema datim uputstvima.<br>
U slučaju povrede ili iznenadnog oboljenja POSLODAVAC će bez odlaganja obezbijediti pružanje prve i hitne medicinske pomoći.</div>

<div class="clan-title">Član 22.</div>
<div class="clan-body">POSLODAVAC je dužan da ZAPOSLENOG/U obavezno osigura od povreda na radu, profesionalnih bolesti i bolesti u vezi sa radom.<br>
Troškove i premiju osiguranja u cjelosti snosi POSLODAVAC.</div>

<div class="clan-title">Član 23.</div>
<div class="clan-body">POSLODAVAC je dužan da ZAPOSLENOM/OJ, bez naknade, nabavi i izda na upotrebu odgovarajuća sredstva i opremu lične zaštite na radu, u skladu sa Aktom o procjeni rizika, propisima, standardima i Normativom sredstava i opreme lične zaštite na radu.<br>
ZAPOSLENI/A je dužan/na da sredstva i opremu lične zaštite koristi namjenski, da ih održava u ispravnom stanju i da odmah prijavi njihovo oštećenje, neispravnost ili gubitak.<br>
Normativ sredstava i opreme lične zaštite na radu koja pripada ZAPOSLENOM/OJ čini sastavni dio ovog ugovora kao Prilog broj 1 i izrađuje se u skladu sa Aktom o procjeni rizika za radno mjesto ${radnoMjesto}.</div>

<div class="clan-title">Član 24.</div>
<div class="clan-body">Ovaj Ugovor o radu zaključen je između POSLODAVCA i ZAPOSLENOG/E dana ${datumSticanja} godine.</div>

<div class="clan-title">Član 25.</div>
<div class="clan-body">Ovaj Ugovor o radu je sačinjen u tri (3) istovjetna primjerka, od kojih jedan zadržava ZAPOSLENI/A, a dva POSLODAVAC.</div>

<div class="signatures">
<div class="sig-block"><div class="sig-line">Zaposleni,<br>${zaposleni}</div></div>
<div class="mp">M.P.</div>
<div class="sig-block"><div class="sig-line">Poslodavac,<br>${direktor}</div></div>
</div>

</body></html>`;
}

function generateUgovorDopunski(entry, client) {
  const firma = client?.naziv_firme || '________________________________________';
  const direktor = client?.direktor || '________________________________________';
  const grad = client?.grad || '________________';
  const danas = formatDatePrint(new Date().toISOString().split('T')[0]);
  const zaposleni = `${(entry.prezime || '').toUpperCase()} ${(entry.ime || '').toUpperCase()}`;
  const prebivaliste = entry.prebivaliste || '________________';
  const jmb = entry.jmb || '________________';
  const lickarta = entry.broj_dokumenta || '________________';
  const kvalifikacija = entry.kvalifikacija || '________________';
  const nivo = entry.nivo_obrazovanja || '________________';
  const radnoMjesto = entry.naziv_radnog_mjesta || '________________';
  const opisPoslova = entry.opis_poslova || radnoMjesto;
  const tipOsnovnog = entry.tip_rada === 'odredjeno' ? 'određeno' : 'neodređeno';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Ugovor o dopunskom radu - ${zaposleni}</title>
<style>
@page{size:A4;margin:18mm 20mm}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;font-size:12pt;color:#000;line-height:1.5}
p{text-align:justify;margin:6px 0}
.title{text-align:center;font-weight:bold;font-size:16pt;margin:20px 0 6px;text-transform:uppercase}
.subtitle{text-align:center;font-weight:bold;font-size:14pt;margin-bottom:16px;text-transform:uppercase}
.clan{font-weight:bold;text-align:center;margin:14px 0 4px;font-size:12pt}
.signatures{display:flex;justify-content:space-between;margin-top:50px}
.sig-block{text-align:center}
.sig-line{border-top:1px solid #000;min-width:180px;padding-top:4px;margin-top:50px}
.mp{margin-top:50px}
@media print{body{margin:0}}
</style></head><body>

<div class="title">UGOVOR</div>
<div class="subtitle">O Dopunskom Radu</div>

<p>Zaključen u ${grad}, dana ${danas} godine, između:</p>
<p>Upravnog odbora privrednog društva "<b>${firma}</b>", koje zastupa ${direktor}, kao <b>Poslodavca</b> i</p>
<p><b>${zaposleni}</b>, ${prebivaliste}, broj lične karte ${lickarta}, JMBG ${jmb}, sa prebivalištem u ${grad}, kao <b>Zaposlenog</b>.</p>
<p>Na osnovu člana 49 Zakona o radu ("Službeni list Crne Gore", br. 074/19, 008/21, 059/21, 068/21, 145/21) i na osnovu Statuta privrednog društva "<b>${firma}</b>".</p>

<div class="clan">Član 1</div>
<p>Ovim Ugovorom se uređuju međusobni odnosi, prava, obaveze i odgovornosti iz rada i po osnovu rada između Poslodavca i Zaposlenog u skladu sa odredbama Zakona o radu i ostalim pozitivnim zakonskim propisima kojima je regulisana oblast radnih odnosa u Crnoj Gori, uključujući i zakone i podzakonske akte kojima se propisana obaveza plaćanja poreza na dohodak - naknada koja se ostvaruje po osnovu ovog ugovora.</p>
<p>Zaposleni ima ${kvalifikacija}${nivo ? ' (' + nivo + ')' : ''}, a po osnovu Ugovora o radu na ${tipOsnovnog} vrijeme zaposlen je kod ${firma} gdje ostvaruje puno radno vrijeme od 40 časova sedmično.</p>
<p>Zaposleni se angažuje za obavljanje sljedećih poslova: <b>${opisPoslova}</b>.</p>

<div class="clan">Član 2</div>
<p>Zaposleni će poslove, koji su predmet ovog ugovora, navedenih u članu 1. stav 3., obavljati počev od datuma potpisivanja ugovora, narednih 12 (dvanaest) mjeseci.</p>
<p>Zaposleni će poslove, koji su predmet ovog ugovora, navedenih u članu 1. stav 3., obavljati 10 sati sedmično i to u periodu od 2 sata dnevno, svakog radnog dana, od 18h-20h.</p>
<p>Vrijeme obavljanja poslova u toku dana, zaposleni može odrediti drugačije.</p>
<p>Mjesto obavljanja poslova je ${grad}.</p>
<p>Datum potpisivanja ugovora predstavlja i dan stupanja na rad.</p>

<div class="clan">Član 3</div>
<p>Za obavljanje poslova iz člana 1 ovog ugovora Zaposlenom pripada mjesečna zarada u iznosu koji je određen posebnom Tarifom Poslodavca, sa kojom je zaposleni u potpunosti upoznat, a koja čini sastavni dio ovog Ugovora.</p>
<p>Poslodavac će isplaćivati zaradu i uredno izmirivati propisane poreze i doprinose na zaradu zaposlenog iz stava 1. ovog člana, najkasnije do 25. u mjesecu, za prethodni mjesec.</p>

<div class="clan">Član 4</div>
<p>Za ostvarivanje radnih rezultata iznad prosjeka, za određeni period, poslodavac može zaposlenom obračunati odgovarajuću stimulaciju na osnovu opštih akata društva i postojeće Tarife.</p>

<div class="clan">Član 5</div>
<p>Poslodavac se obavezuje da obezbijedi i sprovodi zaštitu na radu u skladu sa zakonom, kolektivnim ugovorom, odnosno propisanim mjerama i normativima zaštite na radu Poslodavca.</p>

<div class="clan">Član 6</div>
<p>Zaposleni je dužan da se pridržava obaveza propisanih zakonom, normativnim aktima poslodavca i Ugovorom o radu.</p>

<div class="clan">Član 7</div>
<p>Zaposleni je odgovoran za štetu koju je, u toku rada ili u vezi sa radom, namjerno ili krajnjom nepažnjom prouzrokovao poslodavcu.</p>
<p>Ako je štetu prouzrokovalo više zaposlenih, svaki zaposleni je odgovoran za dio štete koju je prouzrokovao.</p>
<p>Ako se za zaposlenog iz stava 2. ovog člana ne može utvrditi dio štete koju je prouzrokovao, smatraće se da su svi zaposleni podjednako odgovorni i štetu nadoknađuju u jednakim djelovima.</p>

<div class="clan">Član 8</div>
<p>Zaposlenom prestaje radni odnos:</p>
<p>1) istekom roka na koji je ugovor zaključen;</p>
<p>2) sporazumom između zaposlenog i poslodavca;</p>
<p>3) pisanim otkazom ugovora od strane poslodavca ili zaposlenog.</p>

<div class="clan">Član 9</div>
<p>O pravima i obavezama zaposlenih iz rada i po osnovu rada odlučuje direktor, odnosno izvršni direktor, u skladu sa zakonom, normativnim aktima poslodavca i Ugovorom o dopunskom radu.</p>

<div class="clan">Član 10</div>
<p>Zaposleni i poslodavac saglasni su da se na sva prava, obaveze i odgovornosti koji nisu uređeni ovim ugovorom, primjenjuju odgovarajuće odredbe zakona, osnivačkog akta, statuta i drugih opštih akata poslodavca.</p>
<p>Eventualne sporove u vezi sa ovim Ugovorom, ugovorne strane će rješavati sporazumno, a ukoliko to nije moguće, nadležan je Osnovni sud u ${grad}.</p>

<div class="clan">Član 11</div>
<p>Ovaj ugovor je sačinjen u 4 (četiri) primjerka, od kojih svaka ugovorna strana zadržava po 2 (dva) primjerka.</p>
<p>U ${grad}, dana ${danas}.</p>

<div class="signatures">
<div class="sig-block"><div class="sig-line">Zaposleni<br>${zaposleni}</div></div>
<div class="mp">M.P.</div>
<div class="sig-block"><div class="sig-line">Poslodavac<br>${direktor}</div></div>
</div>

</body></html>`;
}

function generateUgovorOdredjeno(entry, client) {
  const radnoVrijeme = parseInt(entry.radno_vrijeme) || 40;
  const isPuno = radnoVrijeme >= 40;
  // Naziv radnog mjesta, Sati nedjeljno, Datum sticanja, Datum isteka i Zarada
  // moraju biti BOLD + UNDERLINE u ugovoru (zahtjev korisnika).
  const radnoVrijemeText = isPuno
    ? `sa punim radnim vremenom od <u><b>40 časova nedjeljno</b></u>`
    : `sa nepunim radnim vremenom od <u><b>${radnoVrijeme} časova nedjeljno</b></u>`;

  const firma = client?.naziv_firme || '________________________________________';
  const direktor = client?.direktor || '________________________________________';
  const grad = client?.grad || '________________';
  const zarada = `<u><b>${formatZarada(entry.zarada)}</b></u>`;
  const datumSticanja = `<u><b>${formatDatePrint(entry.datum_sticanja) || '__.__.____'}</b></u>`;
  const datumIsteka = `<u><b>${formatDatePrint(entry.datum_isteka) || '__.__.____'}</b></u>`;
  const zaposleni = `${(entry.prezime || '').toUpperCase()} ${(entry.ime || '').toUpperCase()}`;
  const jmb = entry.jmb || '________________';
  const kvalifikacija = entry.kvalifikacija || '';
  const radnoMjesto = `<u><b>${(entry.naziv_radnog_mjesta || '').toUpperCase()}</b></u>`;
  const prebivaliste = entry.prebivaliste || '';
  const nivo = entry.nivo_obrazovanja || '';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Ugovor na određeno - ${zaposleni}</title>
<style>
@page { size: A4; margin: 15mm 18mm; }
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family: Arial, Helvetica, sans-serif; font-size: 12pt; color: #000; line-height: 1.5; }
.header { font-size: 10pt; text-align: justify; line-height: 1.4; margin-bottom: 16px; }
.title { text-align: center; font-weight: bold; font-size: 14pt; margin: 14px 0 4px; text-transform: uppercase; }
.subtitle { text-align: center; font-size: 12pt; margin-bottom: 14px; }
.party { margin: 6px 0; font-size: 12pt; line-height: 1.5; }
.clan-title { font-weight: bold; text-align: center; margin: 12px 0 4px; font-size: 12pt; }
.clan-body { text-align: justify; margin: 0 0 4px; font-size: 12pt; line-height: 1.5; }
.clan-body ul { margin-left: 12px; }
.clan-body li { margin-bottom: 2px; }
.section-title { font-weight: bold; text-align: center; font-size: 12pt; margin: 14px 0 6px; font-style: italic; }
.signatures { display: flex; justify-content: space-between; margin-top: 50px; }
.sig-block { text-align: center; font-size: 12pt; }
.sig-line { border-top: 1px solid #000; min-width: 160px; margin-top: 50px; padding-top: 4px; }
.mp { margin-top: 50px; font-size: 12pt; }
@media print { body { margin:0; padding:0; } }
</style></head><body>

<div class="header">
Na osnovu člana 37 u vezi sa članom 29 Zakona o radu ("Službeni list Crne Gore", br. 074/19 od 30.12.2019, 008/21 od 26.01.2021, 059/21 od 04.06.2021, 068/21 od 23.06.2021, 145/21 od 31.12.2021), Opšteg kolektivnog ugovora (\u201eSlužbeni list Crne Gore\u201c, br. 150/2022 od 30.12.2022), člana 1 Zakona o zaštiti na radu ("Sl. list RCG", br. 79/04 od 23.12.2004 i "Sl. list Crne Gore", br. 26/10 od 07.05.2010, 40/11 od 08.08.2011) i Pravilnika o pitanjima iz zaštite na radu koje treba urediti ugovorom o radu (\u201eSl.list RCG br.67/05\u201c) zaključuje se:
</div>

<div class="title">UGOVOR O RADU NA ODREĐENO VRIJEME</div>
<div class="subtitle">zaključen između</div>

<p class="party">1. ${firma}, ${direktor} (u daljem tekstu poslodavac) i</p>
<p class="party">2. ${zaposleni}, ${prebivaliste ? prebivaliste + ', ' : ''}${grad}, JMBG ${jmb}, ${nivo}${kvalifikacija ? ', ' + kvalifikacija : ''}, (u daljem tekstu: zaposleni)</p>

<div class="clan-title">Član 1.</div>
<div class="clan-body">Zaposleni zasniva radni odnos na radno mjesto ${radnoMjesto}. Pored opštih uslova za zasnivanje radnog odnosa radnik ispunjava i posebne uslove za rad i to stručna sprema, radno iskustvo i dr.</div>

<div class="clan-title">Član 2.</div>
<div class="clan-body">Na predmetnom radnom mjestu ZAPOSLENI/A je dužan da:
<ul>
<li>savjesno i odgovorno obavlja poslove radnog mjesta,</li>
<li>poštuje organizaciju rada i poslovanje kod POSLODAVCA, kao i uslove i pravila POSLODAVCA u vezi sa ispunjavanjem ugovorenih i drugih obaveza iz radnog odnosa,</li>
<li>vodi računa i savjesno se odnosi prema sredstvima rada i materijalnim sredstvima POSLODAVCA,</li>
<li>u potpunosti poštuje propise o zaštiti i zdravlju na radu.</li>
<li>Obavlja i ostale poslove iz djelokruga radnog mjesta prema nalogu pretpostavljenih, kao oblik opšte obaveze lojalnosti prema POSLODAVCU i poštovanja obaveza koje proizlaze iz radnog odnosa.</li>
</ul></div>

<div class="clan-title">Član 3.</div>
<div class="clan-body">ZAPOSLENI/A zasniva radni odnos na određeno vrijeme, počev od ${datumSticanja} do ${datumIsteka} godine u organizacionoj jedinici ${firma} u ${grad}u, s tim što u skladu sa potrebama rada u određenom vremenskom periodu (turistička sezona) može biti raspoređen/a i u drugu organizacionu jedinicu na teritoriji opštine ${grad}.</div>

<div class="clan-title">Član 4.</div>
<div class="clan-body">ZAPOSLENI/A zasniva radni odnos ${radnoVrijemeText}. Rad se obavlja u smjenama prema mjesečnom rasporedu koji će uraditi POSLODAVAC.<br>
Radi racionalnijeg korišćenja radnog vremena POSLODAVAC može izvršiti preraspodjelu radnog vremena u skladu sa članom 68 Zakona o radu.</div>

<div class="clan-title">Član 5.</div>
<div class="clan-body">ZAPOSLENI/A ima pravo na odmor i to:
<ul>
<li>U toku radnog dana u trajanju od najmanje 30 minuta (pauza). Odluku o rasporedu i načinu korišćenja odmora u toku radnog dana donosi nadležni organ POSLODAVCA,</li>
<li>Dnevni odmor između dva uzastopna radna dana u trajanju od najmanje 12 časova neprekidno,</li>
<li>Sedmični odmor u trajanju od najmanje 24 časa, koji se u pravilu koristi nedjeljom. Ako priroda posla i organizacija rada to zahtijeva, POSLODAVAC je dužan da odredi drugi dan za korišćenje ovog odmora, o čemu unaprijed za naredni mjesec sačinjava raspored i blagovremeno obavještava ZAPOSLENOG/U,</li>
<li>Godišnji odmor u trajanju od najmanje dvadeset (20) radnih dana. Trajanje godišnjeg odmora utvrđuje se srazmjerno vremenu provedenom u radnom odnosu, s tim da se obračunava i uvećava na osnovu kriterijuma utvrđenih Zakonom o radu i Opštim kolektivnim ugovorom.</li>
</ul></div>

<div class="clan-title">Član 6.</div>
<div class="clan-body">Zarada ZAPOSLENOG/E utvrđena je u neto iznosu od ${zarada} koji predstavlja iznos minimalne zarade u skladu sa Zakonom o radu i isplaćivaće se najkasnije do 10-og u mjesecu za prethodni mjesec rada.</div>

<div class="clan-title">Član 7.</div>
<div class="clan-body">ZAPOSLENI/A je dužan da se na radu pridržava obaveza propisanih Zakonom o radu, Opštim kolektivnim ugovorom i ugovorom o radu.<br>
ZAPOSLENI/A koji umišljajem ili iz nehata povrijedi radnu obavezu ili se ne pridržava odluka, usmenih naloga ili uputstava koje je donio POSLODAVAC, odgovara za učinjenu povredu radne obaveze.</div>

<div class="clan-title">Član 8.</div>
<div class="clan-body">Radni odnos ZAPOSLENOM/OJ će prestati istekom ugovora o radu na određeno vrijeme na koje je zaključen ovaj ugovor.</div>

<div class="clan-title">Član 9.</div>
<div class="clan-body">Za sva ostala prava i obaveze između ZAPOSLENOG/E i POSLODAVCA koja nijesu utvrđena ovim Ugovorom o radu primjenjivaće se odredbe iz Zakona o radu i Opšteg kolektivnog ugovora koji su na snazi u momentu utvrđivanja prava između ZAPOSLENOG/E i POSLODAVCA.</div>

<div class="section-title">MJERE ZAŠTITE I ZDRAVLJA NA RADU, NJIHOVO SPROVOĐENJE, PRAVA, OBAVEZE I ODGOVORNOSTI ZAPOSLENOG/E</div>

<div class="clan-title">Član 10.</div>
<div class="clan-body">POSLODAVAC je dužan da organizacijom rada i procesa rada obezbijedi ZAPOSLENOM/OJ bezbjedne i zdrave uslove rada, u skladu sa zakonom, podzakonskim aktima, Aktom o procjeni rizika, tehničkim propisima, standardima i pravilima struke.<br>
POSLODAVAC je dužan da ZAPOSLENOG/U, prije početka rada, upozna sa svim opasnostima, štetnostima i rizicima na radnom mjestu na koje je raspoređen, kao i sa konkretnim mjerama zaštite i zdravlja na radu potrebnim radi sprečavanja povrede na radu, profesionalne bolesti ili bolesti u vezi sa radom.<br>
POSLODAVAC je dužan da ZAPOSLENOM/OJ obezbijedi odgovarajuća pisana uputstva za bezbjedan i zdrav rad.</div>

<div class="clan-title">Član 11.</div>
<div class="clan-body">POSLODAVAC je dužan da izvrši teorijsko i praktično osposobljavanje ZAPOSLENOG/E za bezbjedan i zdrav rad:<br>
– prije raspoređivanja na radno mjesto;<br>
– prilikom raspoređivanja na drugo radno mjesto;<br>
– prilikom uvođenja nove tehnologije;<br>
– prilikom uvođenja novih ili zamjene postojećih sredstava za rad;<br>
– prilikom promjene procesa rada; i<br>
– prilikom ponovnog raspoređivanja na rad nakon odsustva sa rada koje je trajalo duže od jedne godine.<br>
Osposobljavanje i provjera osposobljenosti obavljaju se u toku radnog vremena, a sve troškove snosi POSLODAVAC.</div>

<div class="clan-title">Član 12.</div>
<div class="clan-body">ZAPOSLENI/A ima pravo i obavezu da se prije početka rada upozna sa mjerama zaštite i zdravlja na radu na poslovima radnog mjesta na koje je raspoređen i da se osposobljava za njihovo sprovođenje.<br>
ZAPOSLENI/A ima pravo i obavezu da POSLODAVCU daje predloge, primjedbe i obavještenja o pitanjima koja se odnose na zaštitu i zdravlje na radu.<br>
ZAPOSLENI/A ima pravo i obavezu da obavi prethodni, periodični ili vanredni zdravstveni pregled koji odgovara rizicima radnog mjesta, kada ga na pregled uputi POSLODAVAC.</div>

<div class="clan-title">Član 13.</div>
<div class="clan-body">ZAPOSLENI/A ima pravo da odbije da radi ako:<br>
– prethodno nije upoznat/a sa opasnostima, štetnostima i rizicima na radu;<br>
– mu/joj POSLODAVAC nije obezbijedio propisani zdravstveni pregled;<br>
– mu/joj prijeti neposredna opasnost po život ili zdravlje zato što nijesu sprovedene propisane mjere zaštite i zdravlja na radu; ili<br>
– na sredstvu za rad nijesu obezbijeđene propisane mjere zaštite i zdravlja na radu, čime su neposredno ugroženi njegov/njen život i zdravlje.<br>
Kada odbije da radi, ZAPOSLENI/A je dužan/na da se pisanim putem obrati POSLODAVCU radi preduzimanja mjera za koje smatra da nijesu sprovedene.<br>
Ako POSLODAVAC smatra da zahtjev ZAPOSLENOG/E nije opravdan, dužan je da odmah obavijesti nadležnu inspekciju zaštite i zdravlja na radu.</div>

<div class="clan-title">Član 14.</div>
<div class="clan-body">U slučaju ozbiljne i neposredne opasnosti po život i zdravlje, ZAPOSLENI/A može preduzeti odgovarajuće mjere u skladu sa svojim znanjem, osposobljenošću i tehničkim sredstvima koja su mu/joj na raspolaganju.<br>
U slučaju ozbiljne, neposredne i neizbježne opasnosti, ZAPOSLENI/A ima pravo da zaustavi rad i napusti opasno radno mjesto, radni proces, odnosno radnu sredinu i uputi se na bezbjedno mjesto.<br>
Zbog postupanja u skladu sa ovim članom ZAPOSLENI/A ne smije biti doveden/a u nepovoljniji položaj, osim ako je postupao/la nepažljivo ili nemarno.</div>

<div class="clan-title">Član 15.</div>
<div class="clan-body">ZAPOSLENI/A je obavezan/na da, u skladu sa svojom osposobljenošću i uputstvima dobijenim od POSLODAVCA:<br>
– primjenjuje propisane mjere zaštite i zdravlja na radu;<br>
– vodi računa o svojoj bezbjednosti i zdravlju, kao i o bezbjednosti i zdravlju drugih zaposlenih i lica na koja može uticati njegov/njen rad;<br>
– pravilno i namjenski koristi sredstva za rad, mašine, uređaje, alate, opasne materije i zaštitne uređaje;<br>
– pravilno i namjenski koristi sredstva i opremu lične zaštite na radu;<br>
– ne uklanja, ne isključuje, ne mijenja i ne oštećuje zaštitne uređaje postavljene na sredstvima za rad;<br>
– poštuje znakove upozorenja, zabrane i obaveznog postupanja; i<br>
– sarađuje sa POSLODAVCEM i stručnim licem za zaštitu i zdravlje na radu radi sprovođenja propisanih mjera.</div>

<div class="clan-title">Član 16.</div>
<div class="clan-body">ZAPOSLENI/A je dužan/na da odmah, pisano ili usmeno, neposredno ili preko predstavnika zaposlenih, obavijesti POSLODAVCA i stručno lice o:<br>
– nepravilnostima i nedostacima na sredstvima za rad;<br>
– oštećenju sredstava i opreme lične zaštite;<br>
– štetnostima i opasnostima u radnoj sredini;<br>
– povredi na radu ili opasnoj pojavi; i<br>
– svakoj drugoj pojavi koja može ugroziti njegov/njen život i zdravlje ili život i zdravlje drugih lica.<br>
Ako POSLODAVAC ne otkloni prijavljene nepravilnosti, štetnosti ili opasnosti u roku od tri dana ili ZAPOSLENI/A smatra da nijesu preduzete odgovarajuće mjere, ZAPOSLENI/A može zatražiti intervenciju nadležne inspekcije zaštite i zdravlja na radu i o tome obavijestiti stručno lice.</div>

<div class="clan-title">Član 17.</div>
<div class="clan-body">ZAPOSLENI/A ne može započeti niti obavljati rad pod uticajem alkohola, droge ili drugih sredstava zavisnosti.<br>
ZAPOSLENI/A je dužan/na da se podvrgne provjeri da li je pod uticajem sredstava zavisnosti, na način i po postupku utvrđenom zakonom i odgovarajućim aktom POSLODAVCA.<br>
O izvršenoj provjeri sačinjava se odgovarajući zapisnik.</div>

<div class="clan-title">Član 18.</div>
<div class="clan-body">ZAPOSLENI/A koji radi na radnom mjestu sa posebnim uslovima rada ili povećanim rizikom dužan/na je da odmah obavijesti POSLODAVCA ako ocijeni da zbog svog zdravstvenog stanja nije sposoban/na da bezbjedno obavlja te poslove.<br>
ZAPOSLENI/A je dužan/na da se odazove na zdravstveni pregled na koji ga/je uputi POSLODAVAC.<br>
Ako se zdravstvenim pregledom utvrdi da ZAPOSLENI/A ne ispunjava posebne zdravstvene uslove za obavljanje poslova radnog mjesta, POSLODAVAC će postupiti u skladu sa zakonom i aktom o sistematizaciji radnih mjesta.</div>

<div class="clan-title">Član 19.</div>
<div class="clan-body">ZAPOSLENI/A je dužan/na da svaku povredu na radu odmah prijavi POSLODAVCU ili neposrednom rukovodiocu, osim ako zbog težine povrede to nije u mogućnosti.<br>
POSLODAVAC je dužan da povredu na radu prijavi nadležnim organima u slučajevima i rokovima propisanim zakonom i da povrijeđenom zaposlenom i zdravstvenoj ustanovi u kojoj je izvršen pregled izda propisani izvještaj o povredi na radu.<br>
ZAPOSLENI/A ostvaruje prava po osnovu povrede na radu, profesionalne bolesti ili bolesti u vezi sa radom u skladu sa propisima kojima se uređuju zdravstveno, penzijsko i invalidsko osiguranje i radni odnosi.</div>

<div class="clan-title">Član 20.</div>
<div class="clan-body">ZAPOSLENI/A podliježe odgovornosti ako se ne pridržava propisanih mjera zaštite i zdravlja na radu ili ne izvršava obaveze utvrđene zakonom, ovim ugovorom, Aktom o procjeni rizika, uputstvima za bezbjedan rad i drugim aktima POSLODAVCA.<br>
Postojanje odgovornosti, vrsta mjere i postupak njenog utvrđivanja određuju se u skladu sa Zakonom o radu, kolektivnim ugovorom i aktima POSLODAVCA.<br>
Obaveze ZAPOSLENOG/E u oblasti zaštite i zdravlja na radu ne oslobađaju POSLODAVCA njegove zakonske odgovornosti za obezbjeđivanje bezbjednih i zdravih uslova rada.</div>

<div class="clan-title">Član 21.</div>
<div class="clan-body">POSLODAVAC je dužan da organizuje pružanje prve pomoći, zaštitu od požara, spašavanje i evakuaciju zaposlenih, da odredi i osposobi odgovarajući broj zaposlenih za sprovođenje tih mjera i obezbijedi potrebna sredstva i opremu.<br>
ZAPOSLENI/A je dužan/na da se upozna sa postupkom pružanja prve pomoći, zaštite od požara i evakuacije i da postupa prema datim uputstvima.<br>
U slučaju povrede ili iznenadnog oboljenja POSLODAVAC će bez odlaganja obezbijediti pružanje prve i hitne medicinske pomoći.</div>

<div class="clan-title">Član 22.</div>
<div class="clan-body">POSLODAVAC je dužan da ZAPOSLENOG/U obavezno osigura od povreda na radu, profesionalnih bolesti i bolesti u vezi sa radom.<br>
Troškove i premiju osiguranja u cjelosti snosi POSLODAVAC.</div>

<div class="clan-title">Član 23.</div>
<div class="clan-body">POSLODAVAC je dužan da ZAPOSLENOM/OJ, bez naknade, nabavi i izda na upotrebu odgovarajuća sredstva i opremu lične zaštite na radu, u skladu sa Aktom o procjeni rizika, propisima, standardima i Normativom sredstava i opreme lične zaštite na radu.<br>
ZAPOSLENI/A je dužan/na da sredstva i opremu lične zaštite koristi namjenski, da ih održava u ispravnom stanju i da odmah prijavi njihovo oštećenje, neispravnost ili gubitak.<br>
Normativ sredstava i opreme lične zaštite na radu koja pripada ZAPOSLENOM/OJ čini sastavni dio ovog ugovora kao Prilog broj 1 i izrađuje se u skladu sa Aktom o procjeni rizika za radno mjesto ${radnoMjesto}.</div>

<div class="clan-title">Član 24.</div>
<div class="clan-body">Ovaj Ugovor o radu zaključen je između POSLODAVCA i ZAPOSLENOG/E dana ${datumSticanja} godine.</div>

<div class="clan-title">Član 25.</div>
<div class="clan-body">Ovaj Ugovor o radu je sačinjen u tri (3) istovjetna primjerka, od kojih jedan zadržava ZAPOSLENI/A, a dva POSLODAVAC.</div>

<div class="signatures">
<div class="sig-block"><div class="sig-line">Zaposleni,<br>${zaposleni}</div></div>
<div class="mp">M.P.</div>
<div class="sig-block"><div class="sig-line">Poslodavac,<br>${direktor}</div></div>
</div>

</body></html>`;
}

// Gender detection helper: returns 'M' or 'Z' from entry.pol or JMB
function detectPol(entry) {
  if (entry?.pol === 'M' || entry?.pol === 'Z') return entry.pol;
  const j = (entry?.jmb || '').toString();
  if (/^\d{13}$/.test(j)) {
    const gc = parseInt(j.substring(9, 12), 10);
    return gc < 500 ? 'M' : 'Z';
  }
  return 'M'; // default
}

export function generateRjesenjePrestanku(entry, client) {
  const pol = detectPol(entry);
  const f = pol === 'Z';

  // Gender-adjusted words
  const zaposlenomDat = f ? 'Zaposlenoj' : 'Zaposlenom';
  const zaposlenomDatLower = f ? 'zaposlenoj' : 'zaposlenom';
  const rasporedjenoDat = f ? 'raspoređenoj' : 'raspoređenom';
  const zaposleniNomCap = f ? 'Zaposlena' : 'Zaposleni';
  const zasnovao = f ? 'zasnovala' : 'zasnovao';

  const firma = (client?.naziv_firme || '________________________________________').toUpperCase();
  const direktor = (client?.direktor || '________________________________________').toUpperCase();
  const grad = client?.grad || '________________';
  const zaposleniIme = `${(entry.prezime || '').toUpperCase()} ${(entry.ime || '').toUpperCase()}`.trim() || '________________';
  const zaposleniJMB = entry.jmb || '_____________';
  const radnoMjesto = (entry.naziv_radnog_mjesta || '________________').toUpperCase();
  const datumPrestanka = formatDatePrint(entry.datum_odjave) || formatDatePrint(entry.datum_isteka) || '__.__.____';
  const datumIsteka = datumPrestanka;
  const prebivaliste = (entry.prebivaliste || grad || '________________').split(',')[0].trim();

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Rješenje o prestanku radnog odnosa - ${zaposleniIme}</title>
<style>
@page { size: A4; margin: 15mm 20mm; }
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; line-height: 1.5; }
p { text-align: justify; margin: 6px 0; }
${DOC_HEADER_CSS}
.preamble { text-align: justify; margin: 14px 0 10px; font-size: 11pt; }
.title { text-align: center; font-weight: bold; font-size: 15pt; margin: 10px 0 2px; text-transform: uppercase; letter-spacing: 1px; }
.subtitle { text-align: center; font-weight: bold; font-size: 13pt; margin-bottom: 12px; text-transform: uppercase; }
.section { font-weight: bold; text-align: center; margin: 12px 0 6px; font-size: 12pt; letter-spacing: 2px; }
.pouka-title { font-weight: bold; text-align: center; margin: 12px 0 4px; font-size: 11pt; }
.dostaviti { margin-top: 16px; font-size: 10.5pt; }
.dostaviti ul { list-style: none; margin-top: 2px; padding-left: 0; }
.dostaviti li { margin: 1px 0; }
.sig-wrap { display: flex; justify-content: flex-end; margin-top: 24px; }
.sig-block { text-align: center; width: 240px; }
.sig-title { font-weight: bold; margin-bottom: 4px; }
.sig-name { margin-top: 2px; font-weight: normal; }
@media print { body { margin: 0; } }
</style></head><body>

${renderDocHeader(client, entry.datum_odjave || entry.datum_isteka)}

<p class="preamble">Na osnovu člana 164 stav 1 tačka 7 Zakona o radu („Službeni list Crne Gore", br. 074/19, 008/21, 059/21, 068/21, 145/21, 077/24, 084/24, 086/24, 122/25, 165/25 i 051/26), kao i odredaba Statuta društva <b>${firma}</b>, izvršni direktor donosi:</p>

<div class="title">RJEŠENJE</div>
<div class="subtitle">O PRESTANKU RADNOG ODNOSA</div>

<p>${zaposlenomDat} <b>${zaposleniIme}</b>, JMB <b>${zaposleniJMB}</b>, iz ${prebivaliste}, ${rasporedjenoDat} na radno mjesto <b>${radnoMjesto}</b>, dana <b>${datumPrestanka}.god</b> prestaje radni odnos kod poslodavca <b>${firma}</b>, usljed isteka ugovora o radu zaključenog na određeno vrijeme, sa pripadajućim aneksima.</p>
<p>Ovo rješenje proizvodi pravno dejstvo danom isteka ugovora o radu.</p>

<div class="section">O b r a z l o ž e n j e</div>

<p>Uvidom u personalnu evidenciju zaposlenih i dokumentaciju poslodavca, utvrđeno je da ${f ? 'je zaposlena' : 'je zaposleni'} <b>${zaposleniIme}</b>, JMB <b>${zaposleniJMB}</b>, ${zasnovao} radni odnos na određeno vrijeme, na radnom mjestu <b>${radnoMjesto}</b>, na osnovu ugovora o radu, sa pripadajućim aneksima, koji je važio do ${datumIsteka} godine.</p>

<p>Kako je predmetni ugovor o radu istekao, a nije produžen niti zaključen novi ugovor o radu, stekli su se zakonski uslovi za prestanak radnog odnosa po sili zakona, u smislu člana 164 stav 1 tačka 7 Zakona o radu.</p>

<p>Imajući u vidu navedeno činjenično stanje i relevantne zakonske odredbe, odlučeno je kao u dispozitivu ovog rješenja.</p>

<div class="pouka-title">Pravna pouka</div>

<p>Ovo rješenje je konačan pravni akt kod poslodavca.</p>
<p>${zaposleniNomCap} ima pravo da, radi zaštite svojih prava, pokrene postupak pred nadležnim sudom u zakonom propisanom roku.</p>

<div class="dostaviti">
<b>Dostaviti:</b>
<ul>
<li>- ${zaposlenomDatLower}</li>
<li>- arhivi</li>
</ul>
</div>

<div class="sig-wrap">
<div class="sig-block">
<div class="sig-title">Direktor</div>
<div class="sig-name">${direktor}</div>
</div>
</div>

</body></html>`;
}

// ========== RJEŠENJE O OTKAZU UGOVORA O RADU — NEOPRAVDANO IZOSTAJANJE (čl. 172 st. 1 t. 2 al. 3 Zakona o radu) ==========
export function generateRjesenjeOtkazNeopravdano(entry, client) {
  const f = (entry.pol === 'zensko');
  const zaposlenom = f ? 'Zaposlenoj' : 'Zaposlenom';
  const zaposlenomDatLower = f ? 'zaposlenoj' : 'zaposlenom';

  const direktor = (client?.direktor || '________________________________________').toUpperCase();

  const zaposleniIme = `${(entry.prezime || '').toUpperCase()} ${(entry.ime || '').toUpperCase()}`.trim() || '________________';
  const zaposleniJMB = entry.jmb || '________________';
  const radnoMjesto = (entry.naziv_radnog_mjesta || '________________').toUpperCase();
  const datumUgovora = formatDatePrint(entry.datum_zakljucenja_ugovora) || formatDatePrint(entry.datum_sticanja) || formatDatePrint(entry.datum_prijave) || '__.__.____';
  const datumIzostanaka = entry.datum_izostanaka || '__.__.____';
  const datumOdjave = formatDatePrint(entry.datum_odjave) || '__.__.____';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Rješenje o otkazu ugovora o radu - ${zaposleniIme}</title>
<style>
@page { size: A4; margin: 15mm 20mm; }
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; line-height: 1.5; }
p { text-align: justify; margin: 6px 0; }
${DOC_HEADER_CSS}
.preamble { text-align: justify; margin: 14px 0 10px; font-size: 11pt; }
.title { text-align: center; font-weight: bold; font-size: 14pt; margin: 10px 0 14px; text-transform: uppercase; letter-spacing: 1px; }
.section { font-weight: bold; text-align: center; margin: 14px 0 6px; font-size: 12pt; letter-spacing: 2px; }
.pouka-title { font-weight: bold; text-align: center; margin: 14px 0 4px; font-size: 11pt; }
.dostaviti { margin-top: 16px; font-size: 10.5pt; }
.dostaviti ul { list-style: none; margin-top: 2px; padding-left: 0; }
.dostaviti li { margin: 1px 0; }
.sig-wrap { display: flex; justify-content: flex-end; margin-top: 24px; }
.sig-block { text-align: center; width: 240px; }
.sig-title { font-weight: bold; margin-bottom: 4px; }
.sig-name { margin-top: 2px; font-weight: normal; }
@media print { body { margin: 0; } }
</style></head><body>

${renderDocHeader(client, entry.datum_odjave)}

<p class="preamble">Na osnovu člana 172 stav 1 tačka 2 alinea 3, čl. 174 i 175 i člana 177 stav 2 Zakona o radu („Službeni list CG", br. 74/19, 8/21, 59/21, 68/21, 145/21, 77/24, 84/24, 86/24, 122/25, 165/25, 51/26 i 103/26), direktor <b>${direktor}</b>, nakon sprovedenog postupka upozorenja, donosi</p>

<div class="title">RJEŠENJE O OTKAZU UGOVORA O RADU</div>

<p>${zaposlenom} <b>${zaposleniIme}</b>, JMB <b>${zaposleniJMB}</b>, na radnom mjestu <b>${radnoMjesto}</b>, dana <b>${datumOdjave}.god</b> <b>OTKAZUJE SE</b> ugovor o radu od <b>${datumUgovora}</b> god., sa pripadajućim aneksima, zbog neopravdanog izostajanja sa posla.</p>
<p>Zaposleni je neopravdano odsustvovao sa rada tri radna dana uzastopno (${datumIzostanaka}).</p>
<p>Radni odnos prestaje danom urednog dostavljanja ovog rješenja, bez obaveze poštovanja otkaznog roka, na osnovu člana 177 stav 2 Zakona o radu.</p>
<p>Poslodavac će zaposlenom isplatiti sva ostvarena a neisplaćena primanja i uplatiti pripadajuće doprinose u skladu sa članom 179 Zakona o radu, najkasnije u roku od 30 dana od prestanka radnog odnosa. Nadležna služba će sprovesti odjavu sa osiguranja i evidentirati prestanak radnog odnosa.</p>

<div class="section">O b r a z l o ž e n j e</div>

<p>Zaposleni je u radnom odnosu kod poslodavca na osnovu navedenog ugovora. Prema rasporedu rada bio je dužan da radi u danima navedenim u tački 2 ovog rješenja. Činjenice o izostancima utvrđene su na osnovu evidencije dolazak i odlazak na posao zaposlenima.</p>

<div class="pouka-title">Pouka o pravnom lijeku</div>

<p>Zaposleni može, u roku od 15 dana od dana dostavljanja ovog rješenja, pokrenuti postupak pred Agencijom za mirno rješavanje radnih sporova, u skladu sa članom 180a stav 1 i članom 140 Zakona o radu. Ako se spor ne riješi pred Agencijom, postupak pred nadležnim sudom može se pokrenuti u roku od 15 dana od dostavljanja odluke o obustavi postupka mirnog rješavanja spora, u skladu sa članom 141 Zakona o radu.</p>

<div class="dostaviti">
<b>Dostaviti:</b>
<ul>
<li>- ${zaposlenomDatLower}</li>
<li>- arhivi</li>
</ul>
</div>

<div class="sig-wrap">
<div class="sig-block">
<div class="sig-title">DIREKTOR</div>
<div class="sig-name">${direktor}</div>
</div>
</div>

</body></html>`;
}

// ========== RJEŠENJE O OTKAZU UGOVORA O RADU — OTKAZ OD STRANE ZAPOSLENOG (čl. 166 st. 5, u vezi sa st. 1 i 3 Zakona o radu) ==========
export function generateOtkazOdZaposlenog(entry, client) {
  const direktor = (client?.direktor || '________________________________________').toUpperCase();

  const f = (entry.pol === 'zensko');
  const zaposlenom = f ? 'Zaposlenoj' : 'Zaposlenom';
  const zaposlenomDatLower = f ? 'zaposlenoj' : 'zaposlenom';
  const podnioF = f ? 'podnijela' : 'podnio';
  const dostavioF = f ? 'dostavila' : 'dostavio';
  const navelF = f ? 'navela' : 'naveo';

  const zaposleniIme = `${(entry.prezime || '').toUpperCase()} ${(entry.ime || '').toUpperCase()}`.trim() || '________________';
  const zaposleniJMB = entry.jmb || '________________';
  const radnoMjesto = (entry.naziv_radnog_mjesta || '________________').toUpperCase();
  const datumUgovora = formatDatePrint(entry.datum_zakljucenja_ugovora) || formatDatePrint(entry.datum_sticanja) || formatDatePrint(entry.datum_prijave) || '__.__.____';
  const datumPrestanka = formatDatePrint(entry.datum_odjave) || '__.__.____';
  const datumOtkaza = formatDatePrint(entry.datum_otkaza_zaposlenog) || '__.__.____';
  const brojOvjere = entry.broj_ovjere_otkaza || '________';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Rješenje o prestanku radnog odnosa - ${zaposleniIme}</title>
<style>
@page { size: A4; margin: 15mm 20mm; }
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; line-height: 1.5; }
p { text-align: justify; margin: 6px 0; }
${DOC_HEADER_CSS}
.preamble { text-align: justify; margin: 14px 0 10px; font-size: 11pt; }
.title { text-align: center; font-weight: bold; font-size: 14pt; margin: 10px 0 14px; text-transform: uppercase; letter-spacing: 1px; }
.section { font-weight: bold; text-align: center; margin: 14px 0 6px; font-size: 12pt; letter-spacing: 2px; }
.pouka-title { font-weight: bold; text-align: center; margin: 14px 0 4px; font-size: 11pt; }
.sig-wrap { display: flex; justify-content: flex-end; margin-top: 24px; }
.sig-block { text-align: center; width: 240px; }
.sig-title { font-weight: bold; margin-bottom: 4px; }
.sig-name { margin-top: 2px; font-weight: normal; }
@media print { body { margin: 0; } }
</style></head><body>

${renderDocHeader(client, entry.datum_odjave)}

<p class="preamble">Na osnovu člana 166 stav 5, u vezi sa st. 1 i 3 tog člana Zakona o radu („Službeni list CG", br. 74/19, 8/21, 59/21, 68/21, 145/21, 77/24, 84/24, 86/24, 122/25, 165/25, 51/26 i 103/26), postupajući po pisanom i prethodno ovjerenom otkazu zaposlenog, direktor <b>${direktor}</b>, donosi</p>

<div class="title">RJEŠENJE O PRESTANKU RADNOG ODNOSA</div>

<p>${zaposlenom} <b>${zaposleniIme}</b>, JMB <b>${zaposleniJMB}</b>, na radnom mjestu <b>${radnoMjesto}</b>, dana <b>${datumPrestanka}.god</b> <b>PRESTAJE RADNI ODNOS</b> po osnovu otkaza ugovora o radu od <b>${datumUgovora}</b> god., sa pripadajućim aneksima, koji je ${podnioF} zaposleni.</p>
<p>Poslodavac će isplatiti sva ostvarena a neisplaćena primanja i uplatiti pripadajuće doprinose u skladu sa članom 179 Zakona o radu, najkasnije u roku od 30 dana od prestanka radnog odnosa. Nadležna služba će sprovesti odjavu sa osiguranja i evidentirati prestanak radnog odnosa.</p>

<div class="section">O b r a z l o ž e n j e</div>

<p>Zaposleni je dana <b>${datumOtkaza}</b> ${dostavioF} poslodavcu pisani otkaz ugovora o radu, prethodno ovjeren kod notara pod brojem <b>${brojOvjere}</b>. Kao dan prestanka radnog odnosa ${navelF} je <b>${datumPrestanka}</b>.</p>
<p>Otkazni rok: od dostavljanja otkaza do navedenog dana prestanka ostavljeno je najmanje 30 dana, u skladu sa članom 166 stav 3 Zakona o radu.</p>
<p>Imajući u vidu dostavljeni otkaz zaposlenog i ispunjenost uslova iz člana 166 Zakona o radu, odlučeno je kao u izreci.</p>

<div class="pouka-title">Pouka o pravnom lijeku</div>

<p>Zaposleni može, u roku od 15 dana od dana dostavljanja ovog rješenja, pokrenuti postupak pred Agencijom za mirno rješavanje radnih sporova, u skladu sa članom 180a stav 1 i članom 140 Zakona o radu. Ako se spor ne riješi pred Agencijom, postupak pred nadležnim sudom može se pokrenuti u roku od 15 dana od dostavljanja odluke o obustavi postupka mirnog rješavanja spora, u skladu sa članom 141 Zakona o radu.</p>

<div style="margin-top:16px; font-size:10.5pt;">
<b>Dostaviti:</b>
<ul style="list-style:none; padding-left:0; margin-top:2px;">
<li>- ${zaposlenomDatLower}</li>
<li>- arhivi</li>
</ul>
</div>

<div class="sig-wrap">
<div class="sig-block">
<div class="sig-title">DIREKTOR</div>
<div class="sig-name">${direktor}</div>
</div>
</div>

</body></html>`;
}


// ========== SPORAZUM O PRESTANKU RADNOG ODNOSA (čl. 165 Zakona o radu) ==========
export function generateSporazumPrestanak(entry, client) {
  const danas = new Date();
  const dd = String(danas.getDate()).padStart(2, '0');
  const mm = String(danas.getMonth() + 1).padStart(2, '0');
  const yyyy = danas.getFullYear();
  const danasStr = `${dd}.${mm}.${yyyy}`;

  const firma = (client?.naziv_firme || '________________________________________').toUpperCase();
  const sjediste = client?.adresa ? `${client.adresa}${client.grad ? ', ' + client.grad : ''}` : '________________________';
  const pib = client?.pib || '________________';
  const zastupnikIme = client?.direktor || '________________';
  const zastupnikSvojstvo = client?.zastupnik_svojstvo || 'direktor';

  const zaposleniIme = `${(entry.prezime || '').toUpperCase()} ${(entry.ime || '').toUpperCase()}`.trim() || '________________';
  const zaposleniJMB = entry.jmb || '________________';
  const zaposleniAdresa = entry.adresa || '________________';
  const radnoMjesto = (entry.naziv_radnog_mjesta || '________________').toUpperCase();
  const datumUgovora = formatDatePrint(entry.datum_zakljucenja_ugovora) || formatDatePrint(entry.datum_sticanja) || formatDatePrint(entry.datum_prijave) || '__.__.____';
  const datumPrestanka = formatDatePrint(entry.datum_odjave) || '__.__.____';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Sporazum o prestanku radnog odnosa - ${zaposleniIme}</title>
<style>
@page { size: A4; margin: 15mm 20mm; }
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; line-height: 1.5; }
p { text-align: justify; margin: 6px 0; }
.preamble { text-align: justify; margin: 10px 0 14px; font-size: 11pt; }
.title { text-align: center; font-weight: bold; font-size: 14pt; margin: 10px 0 16px; text-transform: uppercase; letter-spacing: 1px; }
.parties { margin: 6px 0 16px; }
.parties ol { margin-left: 24px; }
.parties li { margin: 6px 0; text-align: justify; }
.clan-title { font-weight: bold; text-align: center; margin: 16px 0 6px; font-size: 11.5pt; }
${DOC_HEADER_CSS}
.sig-row { display: flex; justify-content: space-between; margin-top: 36px; }
.sig-col { width: 46%; text-align: center; }
.sig-title { font-weight: bold; margin-bottom: 30px; }
.sig-line { border-bottom: 1px solid #000; width: 100%; height: 28px; }
@media print { body { margin: 0; } }
</style></head><body>

${renderDocHeader(client, entry.datum_odjave)}

<p class="preamble">Na osnovu člana 165 Zakona o radu („Službeni list CG", br. 74/19, 8/21, 59/21, 68/21, 145/21, 77/24, 84/24, 86/24, 122/25, 165/25, 51/26 i 103/26), dana <b>${danasStr}</b>. godine, zaključuje se:</p>

<div class="title">SPORAZUM O PRESTANKU RADNOG ODNOSA</div>

<div class="parties">
<ol>
<li><b>${firma}</b>, sa sjedištem u ${sjediste}, PIB <b>${pib}</b>, kojeg zastupa <b>${zastupnikIme.toUpperCase()}</b>, ${zastupnikSvojstvo}, u daljem tekstu: <b>Poslodavac</b>, i</li>
<li><b>${zaposleniIme}</b>, JMB/broj pasoša <b>${zaposleniJMB}</b>, sa adresom ${zaposleniAdresa}, u daljem tekstu: <b>Zaposleni</b>.</li>
</ol>
</div>

<div class="clan-title">Član 1</div>
<p>Strane saglasno raskidaju ugovor o radu od <b>${datumUgovora}</b>, sa pripadajućim aneksima, na osnovu kojeg Zaposleni obavlja poslove radnog mjesta <b>${radnoMjesto}</b>.</p>
<p>Radni odnos Zaposlenog prestaje dana <b>${datumPrestanka}.god.</b> Navedeni datum ne može prethoditi danu ovjere ovog sporazuma.</p>

<div class="clan-title">Član 2</div>
<p>Sporazum se zaključuje slobodnom i saglasnom voljom obje strane.</p>
<p>Strane potvrđuju da su pročitale i razumjele njegov sadržaj i prihvatile ugovoreni datum prestanka radnog odnosa.</p>

<div class="clan-title">Član 3</div>
<p>Poslodavac će Zaposlenom isplatiti sve neisplaćene zarade, naknade zarade i druga ostvarena primanja do dana prestanka radnog odnosa i uplatiti pripadajuće doprinose, u skladu sa članom 179 Zakona o radu, prije prestanka radnog odnosa, a najkasnije u roku od 30 dana od njegovog prestanka.</p>
<p>Poslodavac će omogućiti korišćenje preostalog godišnjeg odmora prije prestanka radnog odnosa. Ako to ne obezbijedi, Zaposleni ima pravo na naknadu štete u skladu sa članom 86 Zakona o radu.</p>
<p>Ovim sporazumom Zaposleni se ne odriče ostvarenih, a neizmirenih potraživanja.</p>

<div class="clan-title">Član 4</div>
<p>Zaposleni će najkasnije do dana prestanka radnog odnosa izvršiti primopredaju poslova i vratiti zaduženu dokumentaciju, opremu, ključeve i druga sredstva Poslodavca, uz zapisnik.</p>
<p>Poslodavac će sprovesti odjavu sa obaveznog osiguranja i evidentirati prestanak radnog odnosa.</p>

<div class="clan-title">Član 5</div>
<p>Ovaj sporazum proizvodi pravno dejstvo od dana ovjere kod notara, suda ili organa lokalne uprave, u skladu sa članom 165 stav 5 Zakona o radu.</p>
<p>Sačinjen je u tri istovjetna primjerka, po jedan za svaku stranu i jedan za organ ovjere.</p>

<div class="sig-row">
<div class="sig-col">
<div class="sig-title">POSLODAVAC</div>
<div class="sig-line"></div>
<div style="margin-top:4px;">${zastupnikIme.toUpperCase()}</div>
</div>
<div class="sig-col">
<div class="sig-title">ZAPOSLENI</div>
<div class="sig-line"></div>
<div style="margin-top:4px;">${zaposleniIme}</div>
</div>
</div>

</body></html>`;
}


// ========== ODLUKA O OPRAVDANOSTI ZAHTJEVA ZA RADNU DOZVOLU (čl. 69 st. 1 t. 1 Zakona o strancima) ==========
function generateOpravdanostRadneDozvole(entry, client) {
  const danas = new Date();
  const dd = String(danas.getDate()).padStart(2, '0');
  const mm = String(danas.getMonth() + 1).padStart(2, '0');
  const yyyy = danas.getFullYear();
  const danasStr = `${dd}.${mm}.${yyyy}`;

  const djelovodnik = entry.djelovodnik_broj || '______';
  const grad = (client?.grad || 'Ulcinj');
  const firma = (client?.naziv_firme || '_______________').toUpperCase();
  const sjediste = (client?.grad || '_______________').toUpperCase();
  const pib = client?.pib || '_______________';
  const direktor = (client?.direktor || '_______________').toUpperCase();
  const jmbDir = client?.jmb_direktora || '_______________';

  const prezIme = `${(entry.prezime || '').toUpperCase()} ${(entry.ime || '').toUpperCase()}`.trim() || '_______________';
  const brojIsprave = entry.broj_dokumenta || entry.jmb || '_______________';
  const boraviste = (entry.prebivaliste || entry.mjesto || grad || '_______________').toUpperCase();
  const radnoMj = (entry.naziv_radnog_mjesta || '_______________').toUpperCase();
  const datumOd = formatDatePrint(entry.datum_pocetka) || '__.__.____';
  const datumDo = formatDatePrint(entry.datum_isteka) || '__.__.____';
  const zarada = formatZarada(entry.zarada || entry.iznos_zarade || '600');

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Odluka o opravdanosti - ${prezIme}</title>
<style>
@page { size: A4; margin: 20mm 22mm; }
* { margin:0; padding:0; box-sizing:border-box; font-family: Arial, sans-serif; }
body { font-family: Arial, sans-serif; font-size: 12pt; color: #000; line-height: 1.5; }
.hdr { display: flex; justify-content: space-between; margin-bottom: 18px; font-size: 12pt; }
.hdr-left { font-weight: bold; font-size: 12pt; text-transform: uppercase; }
.hdr-right { text-align: right; }
p { text-align: justify; margin: 6px 0; font-size: 12pt; }
.title { text-align: center; font-weight: bold; font-size: 12pt; margin: 16px 0 12px; text-transform: uppercase; line-height: 1.3; }
.clan { text-align: center; font-weight: bold; margin: 12px 0 2px; font-size: 12pt; }
.intro { margin-bottom: 10px; text-align: justify; font-size: 12pt; }
.zaposleni { text-align: center; font-weight: bold; margin-top: 6px; font-size: 12pt; }
.sig-wrap { display: flex; justify-content: space-between; margin-top: 50px; align-items: flex-end; }
.mp { font-weight: bold; font-size: 12pt; padding-bottom: 8px; }
.sig-block { text-align: center; min-width: 220px; font-size: 12pt; }
.sig-line { border-top: 1px solid #000; padding-top: 4px; margin-top: 45px; }
@media print { body { margin: 0; } }
</style></head><body>

<div class="hdr">
  <div class="hdr-left">${firma}</div>
  <div class="hdr-right">
    Djelovodnik br. ${djelovodnik}<br>
    ${grad}, ${danasStr}
  </div>
</div>

<p class="intro">Na osnovu člana 69. stav 1 tačka 1 Zakona o strancima, člana 12 stav 1 alineja 1 Pravilnika o obrascima, bližim uslovima i načinu izdavanja dozvole za privremeni boravak i rad, poslodavac <b>${firma}</b>, dana ${danasStr} godine, donio je odluku kojom dokazuje:</p>

<div class="title">OPRAVDANOST ZAHTJEVA ZA IZDAVANJE DOZVOLE<br>ZA PRIVREMENI BORAVAK I RAD RADI ZAPOŠLJAVANJA</div>

<p><b>${firma}</b>, sa sjedištem društva u ${sjediste} i PIB ${pib}, koje zastupa izvršni direktor <b>${direktor}</b> sa JMB ${jmbDir}.</p>

<p>Zahtjev za opravdanost izdavanja dozvole za privremeni boravak i rad radi zapošljavanja se traži za:</p>

<p class="zaposleni">${prezIme}, JMBG/broj isprave ${brojIsprave}, sa boravištem u ${boraviste}, u daljem tekstu: <b>Zaposleni</b></p>

<div class="clan">Član 1</div>
<p>Zaposleni zasniva radni odnos kod Poslodavca radi obavljanja poslova na radnom mjestu <b>${radnoMj}</b>. Njegovim angažovanjem naše preduzeće može nesmetano da nastavi svoju poslovnu aktivnost u skladu sa važećim propisima.</p>

<div class="clan">Član 2</div>
<p>Radni odnos se zasniva na određeno vrijeme, počev od <b>${datumOd}</b> do <b>${datumDo}</b> godine.</p>

<div class="clan">Član 3</div>
<p>Za izvršavanje povjerenih poslova Zaposlenom pripada mjesečna neto zarada u iznosu od <b>${zarada}</b>, koja se isplaćuje u skladu sa odredbama Zakona o radu, Opšteg kolektivnog ugovora i ovog Ugovora o radu. Poslodavac redovno izmiruje sve zakonske obaveze koje proističu iz rada.</p>

<div class="clan">Član 4</div>
<p>Zaposleni je obavezan da povjerene poslove obavlja savjesno, vodeći računa o ugledu Poslodavca i njegove firme, kao i vodeći računa o svom ponašanju prema korisnicima usluga, te i prema ostalim Zaposlenima u Društvu.</p>

<div class="clan">Član 5</div>
<p>Zaposleni je u radnom odnosu sa punim radnim vremenom u trajanju od 8 sati dnevno, odnosno 40 sati nedeljno.</p>

<div class="clan">Član 6</div>
<p>Zaposleni ima pravo na dnevni, nedeljni i godišnji odmor kao i odsustva sa rada, u skladu sa odredbama Zakona o radu i opštim aktima Poslodavca. Vrijeme korišćenja odmora utvrđuje Poslodavac.</p>

<div class="clan">Član 7</div>
<p>Zaposleni i Poslodavac prihvataju sva prava, obaveze i odgovornosti utvrđene zakonom, kolektivnim ugovorom, Ugovorom o radu i aktima Poslodavca.</p>

<div class="sig-wrap">
  <div class="mp">M.P.</div>
  <div class="sig-block">
    <div>Poslodavac,</div>
    <div class="sig-line">${direktor}</div>
  </div>
</div>

</body></html>`;
}

// ========== MAIN COMPONENT ==========

export default function PrijavaRadnikaPage() {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showDetail, setShowDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('all');
  const [filterStatus, setFilterStatus] = useState('aktivan');
  const [page, setPage] = useState(1);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const [showExpiring, setShowExpiring] = useState(false);
  const [radnaMjesta, setRadnaMjesta] = useState([]);
  // JMB search
  const [jmbSearching, setJmbSearching] = useState(false);
  const [jmbNotFound, setJmbNotFound] = useState(false);
  const [jmbResults, setJmbResults] = useState([]);
  const [satiInfo, setSatiInfo] = useState(null); // { total_sati, records }

  // Check total weekly hours when fizicko lice is selected
  useEffect(() => {
    if (!form.fizicko_lice_id) { setSatiInfo(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/prijava-radnika/sati-check/${form.fizicko_lice_id}`, {
          params: editingId ? { exclude_id: editingId } : {},
        });
        if (!cancelled) setSatiInfo(r.data);
      } catch { if (!cancelled) setSatiInfo(null); }
    })();
    return () => { cancelled = true; };
  }, [form.fizicko_lice_id, editingId]);

  const [showFlForm, setShowFlForm] = useState(false);
  const [flForm, setFlForm] = useState({
    ime: '', prezime: '', jmb: '', djevojacko_prezime: '', ime_roditelja: '',
    datum_rodjenja: '', mjesto_rodjenja: '', opstina_rodjenja: '', drzava_rodjenja: '',
    pol: '', drzavljanstvo: '', vrsta_dokumenta: '', broj_dokumenta: '', dokument_izdat: '',
    stepen_strucnosti: '', zanimanje: '', drzava: '', opstina: '', mjesto: '', adresa: '',
    telefon: '', email: '', client_id: '', napomena: '', aktivan: true,
    vrsta_lica: 'domace',
  });
  const jmbTimer = useRef(null);

  useEffect(() => { fetchAll(); fetchExpiring(); fetchRadnaMjesta(); }, []);
  useEffect(() => { fetchAll(); }, [filterClient, filterStatus]);

  const fetchAll = async () => {
    try {
      const params = {};
      if (filterClient !== 'all') params.client_id = filterClient;
      if (filterStatus !== 'all') params.status = filterStatus;
      const [e, c] = await Promise.all([api.get('/prijava-radnika', { params }), api.get('/clients')]);
      setEntries(e.data);
      setClients(c.data);
    } catch { toast.error('Greska pri ucitavanju'); }
    finally { setLoading(false); }
  };

  const fetchExpiring = async () => {
    try {
      const r = await api.get('/prijava-radnika-expiring?days=15');
      setExpiringContracts(r.data);
    } catch {}
  };

  const fetchRadnaMjesta = async () => {
    try {
      const r = await api.get('/settings/config-lists');
      setRadnaMjesta(r.data.radna_mjesta || []);
    } catch {}
  };

  const sortedClients = useMemo(() =>
    [...clients].sort((a, b) => (a.skraceni_naziv || a.naziv_firme || '').localeCompare(b.skraceni_naziv || b.naziv_firme || '')),
    [clients]);

  const getClientName = (id) => { const c = clients.find(c => c.id === id); return c ? (c.skraceni_naziv || c.naziv_firme) : ''; };
  const getVrstaLabel = (v) => VRSTA_UGOVORA.find(u => u.value === v)?.label || v;

  const filtered = entries.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (e.ime || '').toLowerCase().includes(s) || (e.prezime || '').toLowerCase().includes(s) || (e.jmb || '').includes(s);
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleJmbSearch = async (searchVal) => {
    // Reset linked fizicko_lice whenever user types in the search field — allows picking a different person
    setForm(prev => ({ ...prev, jmb: searchVal, fizicko_lice_id: '' }));
    setJmbNotFound(false);
    setJmbResults([]);
    clearTimeout(jmbTimer.current);
    if (searchVal.length < 3) return;
    jmbTimer.current = setTimeout(async () => {
      setJmbSearching(true);
      try {
        const r = await api.get(`/fizicka-lica?search=${encodeURIComponent(searchVal)}`);
        // Search by JMB exact match first
        const exactJmb = r.data.find(fl => fl.jmb === searchVal);
        if (exactJmb) {
          setForm(prev => ({
            ...prev,
            fizicko_lice_id: exactJmb.id,
            ime: exactJmb.ime || prev.ime,
            prezime: exactJmb.prezime || prev.prezime,
            jmb: exactJmb.jmb,
            prebivaliste: exactJmb.adresa || prev.prebivaliste,
            nivo_obrazovanja: exactJmb.stepen_strucnosti || prev.nivo_obrazovanja,
            kvalifikacija: exactJmb.zanimanje || prev.kvalifikacija,
          }));
          setJmbNotFound(false);
          setJmbResults([]);
          toast.success(`Pronadjeno: ${exactJmb.prezime} ${exactJmb.ime}`);
        } else if (r.data.length > 0) {
          // Show matching results for selection
          setJmbResults(r.data.slice(0, 8));
          setJmbNotFound(false);
        } else {
          // Nothing found - ASK user if they want to create new fizicko lice
          setJmbResults([]);
          setJmbNotFound(true);
          const prompt = /^\d{13}$/.test(searchVal)
            ? `Fizicko lice sa JMB "${searchVal}" ne postoji u bazi. Zelite li da unesete novo fizicko lice?`
            : `Nema rezultata za pretragu "${searchVal}". Zelite li da unesete novo fizicko lice?`;
          if (await confirmDialog(prompt)) {
            handleOpenFlForm();
          }
        }
      } catch {}
      finally { setJmbSearching(false); }
    }, 600);
  };

  const handleSelectFlResult = (fl) => {
    setForm(prev => ({
      ...prev,
      fizicko_lice_id: fl.id,
      ime: fl.ime || prev.ime,
      prezime: fl.prezime || prev.prezime,
      jmb: fl.jmb || prev.jmb,
      prebivaliste: fl.adresa || prev.prebivaliste,
      nivo_obrazovanja: fl.stepen_strucnosti || prev.nivo_obrazovanja,
      kvalifikacija: fl.zanimanje || prev.kvalifikacija,
      vrsta_lica: fl.vrsta_lica || prev.vrsta_lica,
    }));
    setJmbResults([]);
    setJmbNotFound(false);
    toast.success(`Izabrano: ${fl.prezime} ${fl.ime}`);
  };

  const handleOpenFlForm = () => {
    setFlForm(prev => ({
      ...prev,
      jmb: form.jmb,
      ime: form.ime,
      prezime: form.prezime,
      client_id: form.client_id,
    }));
    setShowFlForm(true);
  };

  const handlePasteFlImport = () => {
    navigator.clipboard.readText().then(text => {
      if (!text || text.length < 10) { toast.error('Clipboard je prazan'); return; }
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      const parsed = {};
      const findVal = (...labels) => {
        for (let i = 0; i < lines.length; i++) {
          for (const label of labels) {
            if (lines[i].toLowerCase().includes(label.toLowerCase())) {
              const rest = lines[i].substring(lines[i].toLowerCase().indexOf(label.toLowerCase()) + label.length).trim();
              if (rest && rest.length > 1 && rest.length < 80) return rest;
              for (let j = i + 1; j < Math.min(i + 3, lines.length); j++) {
                const nl = lines[j].trim();
                if (nl && nl.length > 0 && nl.length < 80 && !nl.endsWith(':')) return nl;
              }
            }
          }
        }
        return '';
      };
      const jmbMatch = text.match(/\b(\d{13})\b/);
      if (jmbMatch) parsed.jmb = jmbMatch[1];
      parsed.ime = findVal('Ime', 'IME'); parsed.prezime = findVal('Prezime', 'PREZIME');
      parsed.ime_roditelja = findVal('Ime oca', 'Ime jednog roditelja');
      parsed.datum_rodjenja = findVal('Datum rođenja', 'Datum rodjenja');
      parsed.mjesto_rodjenja = findVal('Mjesto rođenja'); parsed.opstina_rodjenja = findVal('Grad rođenja');
      parsed.drzavljanstvo = findVal('Državljanstvo'); parsed.adresa = findVal('Adresa');
      parsed.mjesto = findVal('Mjesto prebivališta', 'Mjesto stanovanja');
      const kucni = findVal('Kućni broj');
      if (kucni && parsed.adresa) parsed.adresa = `${parsed.adresa} ${kucni}`.trim();
      if (parsed.datum_rodjenja) {
        const months = { 'januar':'01','februar':'02','mart':'03','april':'04','maj':'05','jun':'06','jul':'07','avgust':'08','septembar':'09','oktobar':'10','novembar':'11','decembar':'12' };
        const dm = parsed.datum_rodjenja.match(/(\d{1,2})\.\s*(\w+)\s*(\d{4})/);
        if (dm && months[dm[2].toLowerCase()]) parsed.datum_rodjenja = `${dm[3]}-${months[dm[2].toLowerCase()]}-${dm[1].padStart(2, '0')}`;
      }
      if (parsed.jmb && !parsed.datum_rodjenja) {
        const j = parsed.jmb; const yr = parseInt(j.substring(4,7)) > 900 ? '1'+j.substring(4,7) : '2'+j.substring(4,7).padStart(3,'0');
        parsed.datum_rodjenja = `${yr}-${j.substring(2,4)}-${j.substring(0,2)}`;
      }
      if (parsed.jmb) { const gc = parseInt(parsed.jmb.substring(9,12)); parsed.pol = gc < 500 ? 'M' : 'Z'; }
      setFlForm(prev => ({ ...prev, ...Object.fromEntries(Object.entries(parsed).filter(([_,v]) => v)) }));
      toast.success(`Uvezeno: ${parsed.prezime || ''} ${parsed.ime || ''}`);
    }).catch(() => toast.error('Nije moguce pristupiti clipboard-u'));
  };

  const [showFlPaste, setShowFlPaste] = useState(false);
  const [flPasteText, setFlPasteText] = useState('');
  const handleFlParsePaste = () => {
    if (!flPasteText || flPasteText.length < 5) return;
    const text = flPasteText;
    const lines = text.replace(/[\u200B\u200C\u200D\uFEFF]/g, '').split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const parsed = {};
    const findVal = (...labels) => {
      for (let i = 0; i < lines.length - 1; i++) {
        for (const label of labels) {
          if (lines[i] === label) {
            for (let j = i + 1; j < Math.min(i + 3, lines.length); j++) {
              if (lines[j] && lines[j].length > 0 && lines[j].length < 100) return lines[j];
            }
          }
        }
      }
      return '';
    };
    const jmbMatch = text.match(/\b(\d{13})\b/);
    if (jmbMatch) parsed.jmb = jmbMatch[1];
    parsed.ime = findVal('Ime'); parsed.prezime = findVal('Prezime');
    parsed.ime_roditelja = findVal('Ime oca');
    parsed.datum_rodjenja = findVal('Datum rođenja');
    parsed.mjesto_rodjenja = findVal('Mjesto rođenja'); parsed.opstina_rodjenja = findVal('Grad rođenja');
    parsed.drzavljanstvo = findVal('Državljanstvo'); parsed.adresa = findVal('Adresa');
    parsed.mjesto = findVal('Mjesto prebivališta');
    const kucni = findVal('Kućni broj');
    if (kucni && parsed.adresa) parsed.adresa = `${parsed.adresa} ${kucni}`.trim();
    else if (kucni) parsed.adresa = kucni;
    if (parsed.datum_rodjenja) {
      const months = { 'januar':'01','februar':'02','mart':'03','april':'04','maj':'05','jun':'06','jul':'07','avgust':'08','septembar':'09','oktobar':'10','novembar':'11','decembar':'12' };
      const dm = parsed.datum_rodjenja.match(/(\d{1,2})\.\s*(\w+)\s*(\d{4})/);
      if (dm && months[dm[2].toLowerCase()]) parsed.datum_rodjenja = `${dm[3]}-${months[dm[2].toLowerCase()]}-${dm[1].padStart(2, '0')}`;
    }
    if (parsed.jmb && !parsed.datum_rodjenja) {
      const j = parsed.jmb; const yr = parseInt(j.substring(4,7)) > 900 ? '1'+j.substring(4,7) : '2'+j.substring(4,7).padStart(3,'0');
      parsed.datum_rodjenja = `${yr}-${j.substring(2,4)}-${j.substring(0,2)}`;
    }
    if (parsed.jmb) { const gc = parseInt(parsed.jmb.substring(9,12)); parsed.pol = gc < 500 ? 'M' : 'Z'; }
    setFlForm(prev => ({ ...prev, ...Object.fromEntries(Object.entries(parsed).filter(([_,v]) => v)) }));
    setShowFlPaste(false); setFlPasteText('');
    toast.success(`Uvezeno: ${parsed.prezime || ''} ${parsed.ime || ''}`);
  };

  const handleSaveFlForm = async () => {
    if (!flForm.ime.trim() || !flForm.prezime.trim()) { toast.error('Unesite ime i prezime'); return; }
    if ((flForm.vrsta_lica || 'domace') === 'domace') {
      if (!flForm.jmb || !flForm.jmb.trim()) { toast.error('JMB je obavezan za rezidenta'); return; }
      if (!/^\d{13}$/.test(flForm.jmb.trim())) { toast.error('JMB mora imati tačno 13 cifara'); return; }
    } else if (flForm.jmb && flForm.jmb.trim() && !/^\d{13}$/.test(flForm.jmb.trim())) {
      toast.error('JMB mora imati tačno 13 cifara'); return;
    }
    try {
      const r = await api.post('/fizicka-lica', flForm);
      toast.success(`Fizicko lice kreirano: ${flForm.prezime} ${flForm.ime}`);
      setForm(prev => ({
        ...prev,
        fizicko_lice_id: r.data.id,
        ime: flForm.ime,
        prezime: flForm.prezime,
        jmb: flForm.jmb,
        nivo_obrazovanja: flForm.stepen_strucnosti || prev.nivo_obrazovanja,
        kvalifikacija: flForm.zanimanje || prev.kvalifikacija,
        vrsta_lica: flForm.vrsta_lica || prev.vrsta_lica,
      }));
      setShowFlForm(false);
      setJmbNotFound(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Greska pri kreiranju'); }
  };

  const handleSubmit = async () => {
    // Obavezna polja
    if (!form.client_id) { toast.error('Izaberite klijenta'); return; }
    if (!form.fizicko_lice_id && !(form.ime.trim() && form.prezime.trim())) {
      toast.error('Izaberite fizičko lice iz baze (obavezno)'); return;
    }
    if (!(form.naziv_radnog_mjesta || '').trim()) { toast.error('Unesite naziv radnog mjesta'); return; }
    if (!(form.mjesto_rada || '').trim()) { toast.error('Izaberite mjesto rada'); return; }
    if (!(form.radno_vrijeme || '').toString().trim()) { toast.error('Unesite nedjeljno radno vrijeme (sati)'); return; }
    if (!(form.datum_sticanja || '').trim()) { toast.error('Unesite datum sticanja svojstva osiguranika'); return; }
    if (!(form.zarada || '').toString().trim()) { toast.error('Unesite zaradu'); return; }
    if (form.tip_rada === 'odredjeno' && !(form.datum_isteka || '').trim()) {
      toast.error('Za ugovor na određeno, datum isteka je obavezan'); return;
    }

    // Check total weekly hours across all active prijava records for this fizicko_lice
    if (form.fizicko_lice_id && satiInfo) {
      const currentHours = parseInt(form.radno_vrijeme) || 0;
      const totalHours = satiInfo.total_sati + currentHours;
      if (totalHours > 40) {
        const clientsList = satiInfo.records.map(r => `${r.client_naziv} (${r.radno_vrijeme}h)`).join(', ');
        const ok = await confirmDialog(
          `UPOZORENJE: Ovo lice već ima ${satiInfo.total_sati}h sedmično u drugim firmama (${clientsList}). ` +
          `Sa novom prijavom od ${currentHours}h, ukupno bi bilo ${totalHours}h što prelazi zakonsko ograničenje od 40h.\n\n` +
          `Po zakonu, zaposleni ne može imati više od 40h sedmično. Novi ugovor je moguć samo ako ima ispod 40h.\n\n` +
          `Da li ste sigurni da želite da nastavite?`
        );
        if (!ok) return;
      }
    }

    try {
      if (editingId) {
        await api.put(`/prijava-radnika/${editingId}`, form);
        toast.success('Sacuvano');
      } else {
        if (!form.ime.trim() || !form.prezime.trim()) { toast.error('Unesite ime i prezime'); return; }
        await api.post('/prijava-radnika', form);
        toast.success('Radnik prijavljen');
      }
      setShowForm(false); setEditingId(null); setForm(emptyForm); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Greska'); }
  };

  const handleEdit = (e) => {
    setForm({ ...emptyForm, ...e });
    setEditingId(e.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog('Obrisati prijavu?'))) return;
    try { await api.delete(`/prijava-radnika/${id}`); toast.success('Obrisano'); fetchAll(); }
    catch { toast.error('Greska'); }
  };

  const handleToggleStatus = async (entry) => {
    const newStatus = entry.status === 'aktivan' ? 'odjavljen' : 'aktivan';
    const label = newStatus === 'odjavljen' ? 'odjaviti' : 'aktivirati';
    if (!(await confirmDialog(`${entry.prezime} ${entry.ime} - ${label}?`))) return;
    try {
      const payload = { ...entry };
      delete payload.id; delete payload.created_at; delete payload.created_by; delete payload.updated_at;
      payload.status = newStatus;
      if (newStatus === 'odjavljen' && !payload.datum_odjave) {
        // Ako je ugovor na određeno i istekao je ili ističe danas/u budućnosti,
        // koristi datum isteka kao datum odjave (za tačno računanje staža)
        const today = new Date().toISOString().split('T')[0];
        if (entry.tip_rada === 'odredjeno' && entry.datum_isteka) {
          // Ako je istekao ili ističe na ili prije danas -> koristi datum isteka
          // Ako je datum isteka u budućnosti -> koristi danas (radnik se odjavljuje prije isteka)
          payload.datum_odjave = entry.datum_isteka <= today ? entry.datum_isteka : today;
        } else {
          payload.datum_odjave = today;
        }
      }
      if (newStatus === 'aktivan') {
        payload.datum_odjave = '';
      }
      await api.put(`/prijava-radnika/${entry.id}`, payload);
      toast.success(`${entry.prezime} ${entry.ime} - ${newStatus === 'odjavljen' ? 'odjavljen' : 'aktiviran'}`);
      fetchAll();
    } catch { toast.error('Greska'); }
  };

  // ===== PRINT HANDLERS =====
  const handlePrintUgovor = (entry, type) => {
    const client = clients.find(c => c.id === entry.client_id);
    let html;
    if (type === 'dopunski') html = generateUgovorDopunski(entry, client);
    else if (type === 'prestanak') html = generateRjesenjePrestanku(entry, client);
    else if (type === 'otkaz_neopravdano') html = generateRjesenjeOtkazNeopravdano(entry, client);
    else if (type === 'opravdanost') html = generateOpravdanostRadneDozvole(entry, client);
    else if (type === 'neodredjeno') html = generateUgovorNeodredjeno(entry, client);
    else html = generateUgovorOdredjeno(entry, client);
    printHTML(html);
  };

  const handleQuickPrint = (entry) => {
    let type = 'neodredjeno';
    if (entry.vrsta_ugovora === 'ugovor_dopunski') type = 'dopunski';
    else if (entry.vrsta_ugovora === 'ugovor_odredjeno' || entry.tip_rada === 'odredjeno') type = 'odredjeno';
    handlePrintUgovor(entry, type);
  };

  const handleDownloadWord = (entry, type) => {
    const client = clients.find(c => c.id === entry.client_id);
    let html;
    if (type === 'dopunski') html = generateUgovorDopunski(entry, client);
    else if (type === 'prestanak') html = generateRjesenjePrestanku(entry, client);
    else if (type === 'otkaz_neopravdano') html = generateRjesenjeOtkazNeopravdano(entry, client);
    else if (type === 'opravdanost') html = generateOpravdanostRadneDozvole(entry, client);
    else if (type === 'neodredjeno') html = generateUgovorNeodredjeno(entry, client);
    else html = generateUgovorOdredjeno(entry, client);
    const baseName = type === 'prestanak' ? 'Rjesenje_prestanak'
      : (type === 'otkaz_neopravdano' ? 'Rjesenje_otkaz_neopravdano'
      : (type === 'opravdanost' ? 'Odluka_radna_dozvola' : 'Ugovor'));
    const name = `${baseName}_${(entry.prezime || '').replace(/\s/g,'_')}_${(entry.ime || '').replace(/\s/g,'_')}_${type}.docx`;
    downloadWord(html, name);
  };

  const handleSendRjesenjeEmail = async (entry) => {
    const client = clients.find(c => c.id === entry.client_id);
    if (!client?.email) { toast.error('Klijent nema email adresu'); return; }
    if (!(await confirmDialog(`Poslati Rješenje o prestanku radnog odnosa za ${entry.prezime} ${entry.ime} na ${client.email}?`))) return;
    try {
      const r = await api.post(`/prijava-radnika/${entry.id}/send-rjesenje-prestanku`);
      toast.success(r.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Greška pri slanju'); }
  };

  const handleSendEmail = async (entry) => {
    const client = clients.find(c => c.id === entry.client_id);
    if (!client?.email) { toast.error('Klijent nema email adresu'); return; }
    if (!(await confirmDialog(`Poslati ugovor za ${entry.prezime} ${entry.ime} na ${client.email}?`))) return;
    const type = entry.vrsta_ugovora === 'ugovor_odredjeno' || entry.tip_rada === 'odredjeno' ? 'odredjeno' : 'neodredjeno';
    const html = type === 'neodredjeno'
      ? generateUgovorNeodredjeno(entry, client)
      : generateUgovorOdredjeno(entry, client);
    try {
      const r = await api.post(`/prijava-radnika/${entry.id}/send-email`, { contract_html: html, contract_type: type });
      toast.success(r.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Greska pri slanju'); }
  };

  // ===== EXPIRY HELPERS =====
  const getDaysUntilExpiry = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0,0,0,0);
    const parts = dateStr.split('-');
    const expiry = new Date(parts[0], parts[1]-1, parts[2]);
    return Math.ceil((expiry - today) / (1000*60*60*24));
  };

  const getExpiryBadge = (dateStr) => {
    const days = getDaysUntilExpiry(dateStr);
    if (days === null) return null;
    if (days < 0) return { text: `Istekao (${Math.abs(days)}d)`, cls: 'bg-red-100 text-red-800 border-red-300' };
    if (days === 0) return { text: 'Ističe DANAS!', cls: 'bg-red-100 text-red-800 border-red-300 animate-pulse' };
    if (days <= 5) return { text: `Ističe za ${days}d`, cls: 'bg-amber-100 text-amber-800 border-amber-300' };
    if (days <= 15) return { text: `Ističe za ${days}d`, cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
    return null;
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Ucitavanje...</div>;

  // ===== PRINT WORKER LIST =====
  const handlePrintSpisak = () => {
    if (filterClient === 'all') { toast.error('Izaberite klijenta za štampu spiska'); return; }
    const client = clients.find(c => c.id === filterClient);
    const clientName = client ? (client.skraceni_naziv || client.naziv_firme) : '';
    const clientPib = client ? client.pib : '';
    const workers = filtered.filter(e => e.client_id === filterClient);
    if (workers.length === 0) { toast.error('Nema radnika za ovog klijenta'); return; }

    const today = new Date();
    const dateStr = `${today.getDate().toString().padStart(2,'0')}.${(today.getMonth()+1).toString().padStart(2,'0')}.${today.getFullYear()}`;

    const rows = workers.map((w, i) => `
      <tr>
        <td style="border:1px solid #000;padding:4px 8px;text-align:center;">${i+1}</td>
        <td style="border:1px solid #000;padding:4px 8px;">${(w.prezime || '').toUpperCase()} ${(w.ime || '').toUpperCase()}</td>
        <td style="border:1px solid #000;padding:4px 8px;font-family:monospace;">${w.jmb || ''}</td>
        <td style="border:1px solid #000;padding:4px 8px;">${w.naziv_radnog_mjesta || ''}</td>
        <td style="border:1px solid #000;padding:4px 8px;text-align:center;">${formatDatePrint(w.datum_sticanja) || '-'}</td>
        <td style="border:1px solid #000;padding:4px 8px;text-align:center;">${formatDatePrint(w.datum_isteka) || 'Neodređeno'}</td>
        <td style="border:1px solid #000;padding:4px 8px;text-align:right;">${w.zarada ? formatZarada(w.zarada) : '-'}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Spisak radnika - ${clientName}</title>
<style>
@page { size: A4 landscape; margin: 12mm 15mm; }
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; }
h1 { text-align:center; font-size:14pt; margin-bottom:4px; }
.info { text-align:center; font-size:10pt; color:#333; margin-bottom:12px; }
table { width:100%; border-collapse:collapse; margin-top:8px; }
th { border:1px solid #000; padding:6px 8px; background:#f0f0f0; font-size:10pt; text-align:center; }
td { font-size:10pt; }
.footer { margin-top:20px; font-size:9pt; color:#666; text-align:right; }
@media print { body { margin:0; } }
</style></head><body>
<h1>SPISAK ZAPOSLENIH - ${clientName.toUpperCase()}</h1>
<div class="info">PIB: ${clientPib} | Datum: ${dateStr} | Ukupno radnika: ${workers.length}</div>
<table>
<thead><tr>
<th style="width:30px;">R.br</th>
<th>Ime i prezime</th>
<th>JMB</th>
<th>Radno mjesto</th>
<th>Datum početka</th>
<th>Datum isteka</th>
<th>Neto plata</th>
</tr></thead>
<tbody>${rows}</tbody>
</table>
<div class="footer">CRM FINANCE - FINANCE GROUP DOO | ${dateStr}</div>
</body></html>`;

    printHTML(html);
  };

  return (
    <div className="space-y-6" data-testid="prijava-radnika-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Prijava radnika</h3>
          <p className="text-sm text-slate-600 mt-1">Ljudski resursi - evidencija prijava i ugovora</p>
        </div>
        <div className="flex gap-2">
          {filterClient !== 'all' && (
            <Button onClick={handlePrintSpisak} variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" data-testid="pr-print-spisak">
              <Printer size={16} className="mr-2" /> Štampaj spisak
            </Button>
          )}
          <Button onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="pr-add-btn">
            <Plus size={16} className="mr-2" /> Nova prijava
          </Button>
        </div>
      </div>

      {/* Expiring contracts alert - collapsible */}
      {expiringContracts.length > 0 && (
        <Card className="bg-amber-50 border-amber-300 overflow-hidden" data-testid="expiring-contracts-alert">
          <button onClick={() => setShowExpiring(!showExpiring)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-amber-100/50 transition-colors">
            <Warning size={20} className="text-amber-600 flex-shrink-0" weight="fill" />
            <span className="text-sm font-bold text-amber-800 flex-1">Ugovori / radne dozvole koji uskoro ističu ({expiringContracts.length})</span>
            {showExpiring ? <CaretUp size={16} className="text-amber-600" /> : <CaretDown size={16} className="text-amber-600" />}
          </button>
          {showExpiring && (
            <div className="px-4 pb-4 space-y-1.5">
              <p className="text-xs text-amber-700 mb-2">Domaći: 5 dana prije. Nerezidenti: 60 dana prije. Rok za odjavu: 8 dana od isteka.</p>
              {expiringContracts.map(ec => {
                const days = getDaysUntilExpiry(ec.datum_isteka);
                const isNerezident = ec.vrsta_lica === 'strano';
                const isUrgent = days !== null && days <= 5;
                return (
                  <div key={ec.id} className={`flex items-center gap-2 p-2 rounded text-xs ${isUrgent ? 'bg-red-100 border border-red-200' : isNerezident ? 'bg-orange-100 border border-orange-200' : 'bg-amber-100 border border-amber-200'}`}>
                    <Clock size={14} className={isUrgent ? 'text-red-600' : 'text-amber-600'} />
                    <span className="font-semibold">{ec.prezime} {ec.ime}</span>
                    {isNerezident && <span className="px-1.5 py-0.5 bg-orange-200 text-orange-800 rounded text-[9px] font-bold">NEREZIDENT</span>}
                    <span className="text-slate-500">({getClientName(ec.client_id)})</span>
                    <span className="ml-auto font-bold">
                      {days !== null && days <= 0 ? (
                        <span className="text-red-700">ISTEKAO!</span>
                      ) : days !== null && days <= 5 ? (
                        <span className="text-red-600">za {days} {days === 1 ? 'dan' : 'dana'}</span>
                      ) : (
                        <span className="text-amber-700">za {days} dana</span>
                      )}
                    </span>
                    <span className="text-slate-400">{formatDatePrint(ec.datum_isteka)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Pretrazi ime, prezime, JMB..."
              className="pl-9 bg-white border-slate-300 text-sm" />
          </div>
          <select value={filterClient} onChange={e => { setFilterClient(e.target.value); setPage(1); }}
            className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
            <option value="all">Svi klijenti</option>
            {sortedClients.map(c => <option key={c.id} value={c.id}>{c.skraceni_naziv || c.naziv_firme}</option>)}
          </select>
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
            className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10" data-testid="pr-filter-status">
            <option value="aktivan">Aktivni (default)</option>
            <option value="odjavljen">Odjavljeni (vidi i stranicu Odjava radnika)</option>
            <option value="all">Svi statusi</option>
          </select>
          <p className="text-sm text-slate-500 flex items-center">{filtered.length} zapisa</p>
        </div>
      </Card>

      {paged.length === 0 ? (
        <Card className="p-12 text-center">
          <UserPlus size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Nema prijava radnika</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Klijent</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Prezime i ime</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">JMB</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Vrsta ugovora</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Tip rada</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Datum sticanja</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {paged.map(e => (
                  <tr key={e.id} onDoubleClick={() => setShowDetail(e)} className={`border-b hover:bg-slate-50 cursor-pointer ${e.status === 'odjavljen' ? 'opacity-50 bg-red-50/30' : ''}`}>
                    <td className="px-4 py-3 font-medium">{getClientName(e.client_id)}</td>
                    <td className="px-4 py-3 font-medium">{e.prezime} {e.ime}</td>
                    <td className="px-4 py-3 font-mono text-xs">{e.jmb}</td>
                    <td className="px-4 py-3 text-xs">{getVrstaLabel(e.vrsta_ugovora)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${e.tip_rada === 'neodredjeno' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                        {e.tip_rada === 'neodredjeno' ? 'Neodređeno' : 'Određeno'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">{formatDatePrint(e.datum_sticanja) || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${e.status === 'aktivan' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                        {e.status === 'aktivan' ? 'Aktivan' : 'Odjavljen'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => setShowDetail(e)} className="p-2 hover:bg-blue-100 rounded" data-testid={`pr-view-${e.id}`} title="Detalji"><Eye size={16} className="text-blue-600" /></button>
                        <button onClick={() => handleQuickPrint(e)} className="p-2 hover:bg-green-100 rounded" data-testid={`pr-print-${e.id}`} title="Štampaj ugovor">
                          <Printer size={16} className="text-green-600" />
                        </button>
                        <button onClick={() => handleSendEmail(e)} className="p-2 hover:bg-indigo-100 rounded" title="Pošalji ugovor na mejl klijenta">
                          <EnvelopeSimple size={16} className="text-indigo-600" />
                        </button>
                        <button onClick={() => handleEdit(e)} className="p-2 hover:bg-amber-100 rounded" title="Izmijeni"><Pencil size={16} className="text-amber-600" /></button>
                        <button onClick={() => handleDelete(e.id)} className="p-2 hover:bg-red-100 rounded" title="Obriši"><Trash size={16} className="text-red-600" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="px-4 py-3 bg-slate-50 border-t flex items-center justify-between">
              <span className="text-xs text-slate-500">Str. {page}/{totalPages}</span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1} className="px-2 py-1 text-xs rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-40"><CaretLeft size={12} /></button>
                <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages} className="px-2 py-1 text-xs rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-40"><CaretRight size={12} /></button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pr-form-dialog">
          <DialogHeader><DialogTitle>{editingId ? 'Izmijeni prijavu' : 'Prijava osiguranika'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">

            {/* Klijent */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 font-semibold">Klijent (firma) *</Label>
              <select value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})}
                className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                <option value="">-- Izaberite klijenta --</option>
                {sortedClients.map(c => <option key={c.id} value={c.id}>{c.skraceni_naziv || c.naziv_firme} ({c.pib})</option>)}
              </select>
            </div>

            {/* JMB search */}
            <div className="p-2 border border-slate-300 rounded space-y-1.5">
              <Label className="text-xs font-semibold">Pretraga fizičkog lica * <span className="font-normal text-slate-400">(JMB, ime ili prezime)</span></Label>
              <div className="flex gap-2">
                <Input value={form.jmb} onChange={e => handleJmbSearch(e.target.value)}
                  onDoubleClick={handleOpenFlForm}
                  placeholder="Unesite JMB, ime ili prezime..." className="bg-white border-slate-300 text-sm flex-1 h-8" data-testid="pr-jmb" />
              </div>
              {jmbSearching && <p className="text-xs text-blue-500">Pretrazujem...</p>}
              {jmbResults.length > 0 && (
                <div className="border border-slate-200 rounded bg-white max-h-[200px] overflow-y-auto">
                  {jmbResults.map(fl => (
                    <button key={fl.id} onClick={() => handleSelectFlResult(fl)}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 border-b border-slate-100 flex justify-between items-center">
                      <span><span className="font-semibold">{fl.prezime} {fl.ime}</span> <span className="text-slate-400 font-mono ml-2">{fl.jmb}</span></span>
                      <span className="text-slate-400 text-[10px]">{fl.mjesto || fl.opstina || ''}</span>
                    </button>
                  ))}
                </div>
              )}
              {jmbNotFound && (
                <div className="flex items-center gap-2 p-1.5 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-xs text-amber-700 flex-1">Nije pronadjeno u bazi fizickih lica.</p>
                  <Button size="sm" variant="outline" onClick={handleOpenFlForm} className="text-xs text-blue-600 border-blue-300 hover:bg-blue-50 h-7" data-testid="pr-create-fl-btn">
                    + Kreiraj fizicko lice
                  </Button>
                </div>
              )}
            </div>

            {/* Kompaktna kartica izabranog lica (kad je fizicko_lice_id postavljen) */}
            {form.fizicko_lice_id && (
              <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 text-xs flex flex-wrap gap-x-4 gap-y-0.5">
                <span><strong>{form.prezime} {form.ime}</strong></span>
                <span className="text-slate-600">JMB: <span className="font-mono">{form.jmb}</span></span>
                <span className={`font-semibold ${form.vrsta_lica === 'strano' ? 'text-blue-700' : 'text-green-700'}`}>
                  {form.vrsta_lica === 'strano' ? 'Strano lice' : 'Domaće lice'}
                </span>
                {form.prebivaliste && <span className="text-slate-600">📍 {form.prebivaliste}</span>}
                {form.nivo_obrazovanja && <span className="text-slate-600">Nivo: {form.nivo_obrazovanja}</span>}
                {form.kvalifikacija && <span className="text-slate-600">Kv: {form.kvalifikacija}</span>}
              </div>
            )}

            {/* Ime/Prezime/Prebivalište - samo kad fizicko lice NIJE izabrano (manuelni unos za stara lica) */}
            {!form.fizicko_lice_id && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs text-slate-500 font-semibold">Ime *</Label>
                    <Input value={form.ime} onChange={e => setForm({...form, ime: e.target.value})}
                      className="bg-white border-slate-300 text-sm h-8" data-testid="pr-ime" /></div>
                  <div className="space-y-1"><Label className="text-xs text-slate-500 font-semibold">Prezime *</Label>
                    <Input value={form.prezime} onChange={e => setForm({...form, prezime: e.target.value})}
                      className="bg-white border-slate-300 text-sm h-8" data-testid="pr-prezime" /></div>
                </div>
                <div className="space-y-1"><Label className="text-xs text-slate-500 font-semibold">Prebivalište / boravište</Label>
                  <Input value={form.prebivaliste} onChange={e => setForm({...form, prebivaliste: e.target.value})}
                    placeholder="Adresa stanovanja" className="bg-white border-slate-300 text-sm h-8" /></div>
              </>
            )}

            {/* Radno mjesto */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500 font-semibold">Naziv radnog mjesta *</Label>
                <div className="relative">
                  <Input value={form.naziv_radnog_mjesta} onChange={e => setForm({...form, naziv_radnog_mjesta: e.target.value})} placeholder="Izaberite ili unesite..." className="bg-white border-slate-300 text-sm" list="radna-mjesta-list" />
                  <datalist id="radna-mjesta-list">
                    {radnaMjesta.map(rm => <option key={rm} value={rm} />)}
                  </datalist>
                </div></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 font-semibold">Mjesto rada *</Label>
                <select value={form.mjesto_rada} onChange={e => setForm({...form, mjesto_rada: e.target.value})}
                  className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                  <option value="">Izaberi opštinu...</option>
                  {OPSTINE_CRNE_GORE.map(o => <option key={o} value={o}>{o}</option>)}
                </select></div>
            </div>
            <div className="space-y-1"><Label className="text-xs text-slate-500 font-semibold">Opis poslova</Label>
              <textarea value={form.opis_poslova} onChange={e => setForm({...form, opis_poslova: e.target.value})} placeholder="Kratki opis poslova koje zaposleni obavlja"
                className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[50px] resize-y" /></div>

            {/* Fond časova upozorenje - zbir svih aktivnih prijava za ovo fizičko lice */}
            {satiInfo && satiInfo.records.length > 0 && (() => {
              const current = parseInt(form.radno_vrijeme) || 0;
              const total = satiInfo.total_sati + current;
              const exceeded = total > 40;
              const nearLimit = !exceeded && total >= 40;
              return (
                <div className={`rounded-lg p-3 text-xs border ${exceeded ? 'bg-red-50 border-red-300 text-red-800' : nearLimit ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
                  <div className="font-semibold flex items-center gap-2 mb-1">
                    {exceeded ? '⚠️ UPOZORENJE — PREKORAČEN FOND ČASOVA!' : nearLimit ? '⚠ Fond časova popunjen (40h)' : 'ℹ️ Postojeće aktivne prijave za ovo lice'}
                  </div>
                  <div className="space-y-0.5">
                    {satiInfo.records.map(r => (
                      <div key={r.id} className="flex items-center justify-between gap-2">
                        <span>• <strong>{r.client_naziv}</strong> — {r.radno_vrijeme || '0'}h nedeljno {r.datum_sticanja && `(od ${r.datum_sticanja})`}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 pt-2 border-t border-current opacity-80">
                    Postojeći ukupni fond: <strong>{satiInfo.total_sati}h</strong> + nova prijava: <strong>{current}h</strong> = <strong>{total}h</strong>
                    {exceeded && <div className="mt-1 font-semibold">Zaposleni ne može imati više od 40h sedmično. Smanjite sate u novoj prijavi ili raskinite postojeće.</div>}
                  </div>
                </div>
              );
            })()}

            {/* Radno vrijeme + datum sticanja + zarada + datum odjave */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500 font-semibold">Sati (nedjeljno) *</Label>
                <Input value={form.radno_vrijeme} onChange={e => setForm({...form, radno_vrijeme: e.target.value})} placeholder="40" className="bg-white border-slate-300 text-sm h-8" data-testid="pr-radno-vrijeme" />
                {form.radno_vrijeme && (
                  <p className={`text-[10px] font-medium ${parseInt(form.radno_vrijeme) >= 40 ? 'text-green-600' : 'text-amber-600'}`}>
                    {parseInt(form.radno_vrijeme) >= 40 ? 'Puno' : 'Nepuno'}
                  </p>
                )}
              </div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 font-semibold">Datum sticanja *</Label>
                <DateInput value={form.datum_sticanja} onChange={e => setForm({...form, datum_sticanja: e.target.value})} className="border-slate-300 text-sm h-8" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 font-semibold">Zarada (€) *</Label>
                <Input value={form.zarada} onChange={e => setForm({...form, zarada: e.target.value})} placeholder="npr: 700" className="bg-white border-slate-300 text-sm h-8" data-testid="pr-zarada" /></div>
            </div>

            {/* Tip rada + datum isteka + vrsta ugovora */}
            <div className="grid grid-cols-3 gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 font-semibold">Tip rada *</Label>
                <div className="flex gap-2">
                  <label className={`flex-1 flex items-center gap-1.5 px-2 py-1.5 rounded border-2 cursor-pointer text-xs ${form.tip_rada === 'neodredjeno' ? 'border-green-500 bg-green-50' : 'border-slate-200'}`}>
                    <input type="radio" checked={form.tip_rada === 'neodredjeno'} onChange={() => setForm({...form, tip_rada: 'neodredjeno', datum_isteka: '', vrsta_ugovora: 'ugovor_neodredjeno'})} />
                    Neodr.
                  </label>
                  <label className={`flex-1 flex items-center gap-1.5 px-2 py-1.5 rounded border-2 cursor-pointer text-xs ${form.tip_rada === 'odredjeno' ? 'border-amber-500 bg-amber-50' : 'border-slate-200'}`}>
                    <input type="radio" checked={form.tip_rada === 'odredjeno'} onChange={() => setForm({...form, tip_rada: 'odredjeno', vrsta_ugovora: 'ugovor_odredjeno'})} />
                    Odr.
                  </label>
                </div>
              </div>
              {form.tip_rada === 'odredjeno' ? (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Datum isteka *</Label>
                  <DateInput value={form.datum_isteka} onChange={e => setForm({...form, datum_isteka: e.target.value})} className="border-slate-300 text-sm h-8" />
                </div>
              ) : <div />}
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 font-semibold">Vrsta ugovora</Label>
                <select value={form.vrsta_ugovora} onChange={e => setForm({...form, vrsta_ugovora: e.target.value})}
                  className="flex w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs h-8">
                  {VRSTA_UGOVORA.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div>
            </div>

            {form.status === 'odjavljen' && (
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Datum odjave / prestanka radnog odnosa</Label>
                <DateInput value={form.datum_odjave} onChange={e => setForm({...form, datum_odjave: e.target.value})} className="border-slate-300 text-sm h-8" data-testid="pr-datum-odjave" />
                <p className="text-[10px] text-slate-500">Koristi se za izračun radnog staža (upis u radnu knjižicu)</p>
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Napomena</Label>
              <textarea value={form.napomena} onChange={e => setForm({...form, napomena: e.target.value})}
                className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[50px] resize-y" />
            </div>

            <div className="flex gap-3 justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => setShowForm(false)}>Zatvori</Button>
              <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="pr-save-btn">Sacuvaj</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {showDetail && (
            <>
              <DialogHeader><DialogTitle>{showDetail.prezime} {showDetail.ime} - {getClientName(showDetail.client_id)}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                {[
                  ['Klijent', getClientName(showDetail.client_id)],
                  ['Vrsta lica', showDetail.vrsta_lica === 'domace' ? 'Domaće' : 'Strano'],
                  ['JMB', showDetail.jmb],
                  ['Ime', showDetail.ime],
                  ['Prezime', showDetail.prezime],
                  ['Prebivalište', showDetail.prebivaliste],
                  ['Nivo obrazovanja', showDetail.nivo_obrazovanja],
                  ['Kvalifikacija', showDetail.kvalifikacija],
                  ['Radno mjesto', showDetail.naziv_radnog_mjesta],
                  ['Mjesto rada', showDetail.mjesto_rada],
                  ['Radno vrijeme', showDetail.radno_vrijeme ? `${showDetail.radno_vrijeme}h (${parseInt(showDetail.radno_vrijeme) >= 40 ? 'puno' : 'nepuno'})` : '-'],
                  ['Datum sticanja', formatDatePrint(showDetail.datum_sticanja)],
                  ['Tip rada', showDetail.tip_rada === 'neodredjeno' ? 'Neodređeno' : 'Određeno'],
                  ['Datum isteka', showDetail.tip_rada === 'odredjeno' ? formatDatePrint(showDetail.datum_isteka) : '-'],
                  ['Datum odjave', showDetail.status === 'odjavljen' ? formatDatePrint(showDetail.datum_odjave) : '-'],
                  ['Vrsta ugovora', getVrstaLabel(showDetail.vrsta_ugovora)],
                  ['Zarada', showDetail.zarada ? formatZarada(showDetail.zarada) : '-'],
                  ['Status', showDetail.status === 'aktivan' ? 'Aktivan' : 'Odjavljen'],
                ].map(([l,v]) => (
                  <div key={l} className="p-2 bg-slate-50 rounded border"><span className="text-xs text-slate-500">{l}</span><p className="font-medium">{v || '-'}</p></div>
                ))}
              </div>
              {/* Print and Word buttons in detail */}
              <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t">
                <Button size="sm" onClick={() => { handlePrintUgovor(showDetail, 'neodredjeno'); }}
                  className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid="pr-detail-print-neodredjeno">
                  <Printer size={14} className="mr-1" /> Štampaj neodređeno
                </Button>
                <Button size="sm" onClick={() => { handlePrintUgovor(showDetail, 'odredjeno'); }}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="pr-detail-print-odredjeno">
                  <Printer size={14} className="mr-1" /> Štampaj određeno
                </Button>
                <Button size="sm" onClick={() => handleDownloadWord(showDetail, 'neodredjeno')}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs">
                  Word neodređeno
                </Button>
                <Button size="sm" onClick={() => handleDownloadWord(showDetail, 'odredjeno')}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white text-xs">
                  Word određeno
                </Button>
                <Button size="sm" onClick={() => { handlePrintUgovor(showDetail, 'dopunski'); }}
                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs" data-testid="pr-detail-print-dopunski">
                  <Printer size={14} className="mr-1" /> Štampaj dopunski
                </Button>
                <Button size="sm" onClick={() => handleDownloadWord(showDetail, 'dopunski')}
                  className="bg-purple-500 hover:bg-purple-600 text-white text-xs">
                  Word dopunski
                </Button>
                {/* Odluka o opravdanosti za radnu dozvolu — samo za strane radnike */}
                {showDetail.vrsta_lica === 'strano' && (
                  <>
                    <Button size="sm" onClick={() => { handlePrintUgovor(showDetail, 'opravdanost'); }}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs" data-testid="pr-detail-print-opravdanost">
                      <Printer size={14} className="mr-1" /> Štampaj Odluku o opravdanosti (radna dozvola)
                    </Button>
                    <Button size="sm" onClick={() => handleDownloadWord(showDetail, 'opravdanost')}
                      className="bg-indigo-500 hover:bg-indigo-600 text-white text-xs">
                      Word Odluka o opravdanosti
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Fizicko lice kreiranje */}
      <Dialog open={showFlForm} onOpenChange={setShowFlForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="fl-create-dialog">
          <DialogHeader><DialogTitle>Novo fizicko lice</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-2">
              <Button onClick={() => setShowFlPaste(!showFlPaste)} variant="outline" size="sm" className="text-xs border-green-300 text-green-700 hover:bg-green-50 w-full">
                <ClipboardText size={14} className="mr-1" /> {showFlPaste ? 'Zatvori' : 'Uvezi iz web stranice (e.servis.mup.gov.me)'}
              </Button>
              {showFlPaste && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-2">
                  <p className="text-xs text-green-700">Kopirajte tekst sa e.servis.mup.gov.me i zalijepite ovdje:</p>
                  <textarea value={flPasteText} onChange={e => setFlPasteText(e.target.value)}
                    placeholder="Zalijepite tekst ovdje..."
                    className="w-full rounded-md border border-green-300 bg-white px-3 py-2 text-xs min-h-[80px] resize-y" />
                  <Button onClick={handleFlParsePaste} size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs w-full" disabled={!flPasteText}>
                    Prepoznaj podatke
                  </Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Prezime *</Label>
                <Input value={flForm.prezime} onChange={e => setFlForm({...flForm, prezime: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Ime *</Label>
                <Input value={flForm.ime} onChange={e => setFlForm({...flForm, ime: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>

            {/* Vrsta lica - Rezident / Nerezident */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 font-semibold">Vrsta lica *</Label>
              <div className="flex gap-3">
                <label className={`flex-1 flex items-center gap-2 p-2.5 rounded-lg border-2 cursor-pointer text-sm ${(flForm.vrsta_lica || 'domace') === 'domace' ? 'border-green-500 bg-green-50 text-green-800' : 'border-slate-200 text-slate-600'}`}>
                  <input type="radio" checked={(flForm.vrsta_lica || 'domace') === 'domace'} onChange={() => setFlForm({...flForm, vrsta_lica: 'domace'})} />
                  <span><strong>Rezident (domaće)</strong></span>
                </label>
                <label className={`flex-1 flex items-center gap-2 p-2.5 rounded-lg border-2 cursor-pointer text-sm ${flForm.vrsta_lica === 'strano' ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-slate-200 text-slate-600'}`}>
                  <input type="radio" checked={flForm.vrsta_lica === 'strano'} onChange={() => setFlForm({...flForm, vrsta_lica: 'strano'})} />
                  <span><strong>Nerezident (strano)</strong></span>
                </label>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">JMB {(flForm.vrsta_lica || 'domace') === 'domace' && <span className="text-red-500">*</span>}</Label>
                <Input
                  value={flForm.jmb}
                  onChange={e => setFlForm({...flForm, jmb: e.target.value.replace(/\D/g, '').slice(0, 13)})}
                  maxLength={13}
                  placeholder="13 cifara"
                  className={`bg-white text-sm font-mono ${flForm.jmb && flForm.jmb.length === 13 ? 'border-green-500' : flForm.jmb && flForm.jmb.length > 0 ? 'border-amber-400' : 'border-slate-300'}`}
                />
                {flForm.jmb && flForm.jmb.length > 0 && flForm.jmb.length !== 13 && (
                  <p className="text-[10px] text-amber-600">{flForm.jmb.length}/13 cifara</p>
                )}
              </div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Djevojacko prezime</Label>
                <Input value={flForm.djevojacko_prezime} onChange={e => setFlForm({...flForm, djevojacko_prezime: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Ime roditelja</Label>
                <Input value={flForm.ime_roditelja} onChange={e => setFlForm({...flForm, ime_roditelja: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Datum rodjenja</Label>
                <DateInput value={flForm.datum_rodjenja} onChange={e => setFlForm({...flForm, datum_rodjenja: e.target.value})} className="border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Pol</Label>
                <select value={flForm.pol} onChange={e => setFlForm({...flForm, pol: e.target.value})} className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                  <option value="">-</option><option value="M">Muski</option><option value="Z">Zenski</option>
                </select></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Drzavljanstvo</Label>
                <Input value={flForm.drzavljanstvo} onChange={e => setFlForm({...flForm, drzavljanstvo: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Mjesto rodjenja</Label>
                <Input value={flForm.mjesto_rodjenja} onChange={e => setFlForm({...flForm, mjesto_rodjenja: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Opstina</Label>
                <Input value={flForm.opstina_rodjenja} onChange={e => setFlForm({...flForm, opstina_rodjenja: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Drzava</Label>
                <Input value={flForm.drzava_rodjenja} onChange={e => setFlForm({...flForm, drzava_rodjenja: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Vrsta dokumenta</Label>
                <Input value={flForm.vrsta_dokumenta} onChange={e => setFlForm({...flForm, vrsta_dokumenta: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Broj dokumenta</Label>
                <Input value={flForm.broj_dokumenta} onChange={e => setFlForm({...flForm, broj_dokumenta: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Izdat od</Label>
                <Input value={flForm.dokument_izdat} onChange={e => setFlForm({...flForm, dokument_izdat: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Nivo obrazovanja</Label>
                <select value={flForm.stepen_strucnosti} onChange={e => setFlForm({...flForm, stepen_strucnosti: e.target.value})}
                  className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                  <option value="">Izaberi...</option>
                  {NIVO_OBRAZOVANJA.map(n => <option key={n} value={n}>{n}</option>)}
                </select></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Stručna kvalifikacija</Label>
                <Input value={flForm.zanimanje} onChange={e => setFlForm({...flForm, zanimanje: e.target.value})} placeholder="npr: Ekonomista, Vozač..." className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Drzava</Label>
                <Input value={flForm.drzava} onChange={e => setFlForm({...flForm, drzava: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Opstina</Label>
                <Input value={flForm.opstina} onChange={e => setFlForm({...flForm, opstina: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Mjesto</Label>
                <Input value={flForm.mjesto} onChange={e => setFlForm({...flForm, mjesto: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs text-slate-500">Adresa</Label>
              <Input value={flForm.adresa} onChange={e => setFlForm({...flForm, adresa: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Telefon</Label>
                <Input value={flForm.telefon} onChange={e => setFlForm({...flForm, telefon: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Email</Label>
                <Input value={flForm.email} onChange={e => setFlForm({...flForm, email: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="flex gap-3 justify-end pt-3 border-t">
              <Button variant="outline" onClick={() => setShowFlForm(false)}>Odustani</Button>
              <Button onClick={handleSaveFlForm} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="fl-save-from-pr-btn">Kreiraj i preuzmi</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
